/* Public landing copy. Product labels inside the demos (sidebar, composer,
   agent names such as "WebAgent") intentionally match the signed-in app. */

export type SceneId = 'research' | 'document' | 'data' | 'plugin';
export type Row = [string, string, string];

export interface StepCopy { title: string; actor: string; body?: string; tools?: string[] }
interface SceneBase { id: SceneId; tab: string; prompt: string; steps: StepCopy[] }

export interface ResearchScene extends SceneBase {
  id: 'research'; lead: string; heading: string; table: { head: Row; rows: Row[] }; sources: string;
}
export interface DocumentScene extends SceneBase {
  id: 'document'; fileName: string;
  doc: {
    title: string; overview: [string, string]; market: [string, string[]]; goals: [string, string];
    table: { head: Row; rows: Row[] }; addedHead: string; addedCells: [string, string, string];
    plan: [string, string[]]; risk: [string, string]; addedRisk: string;
  };
  reply: { lead: string; heading: string; items: [string, string]; closing: string };
}
export interface DataScene extends SceneBase {
  id: 'data'; attachment: string; chartTitle: string; target: string; series: [string, number][];
  lead: string; insights: string[]; output: string;
}
export interface PluginScene extends SceneBase {
  id: 'plugin';
  approval: { label: string; title: string; preview: string; fields: [string, string][]; run: string; deny: string };
  lead: string; issues: [string, string][];
}
export type SceneCopy = ResearchScene | DocumentScene | DataScene | PluginScene;
/** One customer story: who, what was built, and the three things it delivers. */
export interface CaseCopy { company: string; kind: string; title: string; body: string; points: [string, string, string] }

export interface LandingCopy {
  nav: { capabilities: string; cases: string; pricing: string; menu: string };
  login: string; signIn: string; loading: string; loginError: string; contact: string; language: string; skip: string;
  hero: { title: string; lead: string; sub: string };
  demo: { label: string; play: string; pause: string; replay: string; url: string };
  ui: {
    personal: string; team: string; workspace: string; teamWorkspace: string; personalSection: string; manage: string;
    agent: string; live: string; docAgent: string; teamManage: string; widget: string; feed: string; tools: string;
    dm: string; storage: string; plugin: string; memory: string; profile: string; profileSub: string;
    placeholder: string; disclaimer: string; welcome: string; steps: string; done: string; saved: string; export: string;
    menus: [string, string, string]; fit: string; paragraph: string;
  };
  scenes: [ResearchScene, DocumentScene, DataScene, PluginScene];
  intro: { eyebrow: string; lead1: string; body1: string; lead2: string; body2: string };
  capabilities: {
    eyebrow: string; title: [string, string]; sub: string;
    cards: { lead: string; body: string; prompt: string }[];
    mocks: {
      research: { heading: string; head: Row; rows: Row[]; sources: string };
      draft: { file: string; title: string; lines: string[]; highlight: string };
      chart: { title: string; months: string[]; file: string };
      slides: string[];
      hwp: { org: string; to: string; subject: string; body: string };
      image: string;
    };
    rosterTitle: string; rosterSub: string; roster: [string, string][];
  };
  docs: {
    eyebrow: string; title: [string, string]; sub: string; points: [string, string][];
    wiki: {
      original: string; wiki: string; project: string; refresh: string; files: [string, string, string]; keyPoints: string; concepts: string[]; edit: string;
      docTitle: string; docSections: [string, string][]; summary: string; coreTitle: string; core: [string, string][];
    };
  };
  integrations: {
    eyebrow: string; title: [string, string]; sub: string;
    cards: [string, string][];
    flow: { from: string; message: string; to: string; result: string };
    approval: { label: string; title: string; preview: string; fields: [string, string][]; run: string; deny: string };
    toggles: { title: string; items: [string, boolean][] };
  };
  teams: {
    eyebrow: string; title: [string, string]; sub: string; cards: [string, string][];
    team: { name: string; role: string; members: string; tokens: string; storage: string; usage: string; created: string };
    skills: { tabs: [string, string, string]; items: [string, string][] };
    chat: { group: string; messages: [string, string][] };
    dash: { title: string; stats: [string, string][]; activity: string };
    controls: [string, string][];
  };
  everyday: {
    eyebrow: string; title: [string, string]; sub: string; cards: [string, string][];
    memory: { title: string; items: [string, string][]; evidence: string };
    live: { status: string; user: string; agent: string };
    translate: { styles: [string, string, string]; source: string; target: string; glossary: string };
    feed: { label: string; items: [string, string][] };
    more: [string, string][];
  };
  cases: {
    eyebrow: string; title: [string, string]; sub: string;
    items: [CaseCopy, CaseCopy, CaseCopy];
    onprem: { title: string; badge: string; rows: [string, string][]; blocked: [string, string] };
    ontology: { title: string; docs: [string, string, string]; concepts: [string, string, string]; agent: string };
    erp: { title: string; extract: string; fields: [string, string][]; done: string; voucher: string };
  };
  faq: { eyebrow: string; title: string; items: { q: string; a: string }[] };
  closing: { title: [string, string]; sub: string };
  footer: { tagline: string; product: string; company: string; legal: string; terms: string; privacy: string; email: string };
}

const ko: LandingCopy = {
  nav: { capabilities: '기능', cases: '도입 사례', pricing: '요금제', menu: '메뉴' },
  login: 'Google로 시작하기', signIn: '시작하기', loading: '로그인으로 이동 중', loginError: '로그인을 시작하지 못했어요. 다시 시도해 주세요.', contact: '도입 문의', language: '언어 선택', skip: '본문으로 건너뛰기',
  hero: {
    title: 'Meet your own AI agent',
    lead: '하나의 화면에서, 하나의 흐름으로.',
    sub: 'askyour.ai는 반복 업무를 자동화하는 올인원 AI 워크스페이스입니다. 사내 환경에 맞춘 온프렘 도입부터 기업 맞춤형 AI 개발까지, 유연하게 지원합니다.',
  },
  demo: { label: '제품 데모', play: '데모 재생', pause: '데모 일시정지', replay: '처음부터 재생', url: 'askyour.ai' },
  ui: {
    personal: '개인', team: '팀', workspace: '워크스페이스', teamWorkspace: '팀 워크스페이스', personalSection: '개인', manage: '관리',
    agent: '통합 에이전트', live: '라이브 에이전트', docAgent: '문서 에이전트', teamManage: '팀 관리', widget: '위젯', feed: '피드', tools: '도구 모음',
    dm: '다이렉트 메시지', storage: '파일 저장소', plugin: '플러그인', memory: '에이전트 메모리', profile: '김지현', profileSub: '노스윈드 영업본부',
    placeholder: '메시지를 입력하세요...', disclaimer: 'Agent는 실수할 수 있습니다. 중요한 정보는 직접 확인하세요.', welcome: '나만의 에이전트와 일상을 함께', steps: '{n}개 단계 진행', done: '완료', saved: '저장됨', export: '내보내기',
    menus: ['파일', '서식', '삽입'], fit: '페이지 맞춤', paragraph: '일반 텍스트',
  },
  scenes: [
    {
      id: 'research', tab: '리서치·보고서',
      prompt: '국내 B2B SaaS 시장의 2026년 주요 트렌드 3가지를 출처와 함께 정리하고, 표로 요약해줘.',
      steps: [
        { title: '요청 이해하는 중', actor: 'RootAgent' },
        { title: '실행 방식 결정', actor: 'RootClassifier → FastActionSelector' },
        { title: '국내 B2B SaaS 시장 트렌드 조사', actor: 'FastActionSelector → WebAgent', body: '업계 리포트와 기사, 공개 자료를 찾아 핵심 트렌드 3가지를 도출합니다.', tools: ['WebAgent가 웹 검색 도구 사용을 완료했습니다', 'WebAgent가 페이지 읽기 도구 사용을 완료했습니다'] },
        { title: '보고서 작성', actor: 'WebAgent → BasicAgent' },
      ],
      lead: '2026년 국내 B2B SaaS 시장은 AI 에이전트 도입과 과금 모델 변화가 성장을 이끌고 있습니다. 주요 트렌드 3가지를 정리했어요.',
      heading: '핵심 트렌드 요약',
      table: { head: ['트렌드', '핵심 변화', '도입 포인트'], rows: [['AI 에이전트 결합', '보조 기능 → 업무 자동 실행', '사내 시스템 연동'], ['버티컬 SaaS 확대', '업종·직무 특화 워크플로', '도입 속도와 ROI'], ['과금 모델 전환', '좌석제 → 사용량·성과 기반', '예산 예측 가능성']] },
      sources: '출처 8개',
    },
    {
      id: 'document', tab: '문서 편집',
      prompt: '3장 목표 표에 "달성 방안" 열을 추가하고, 5장에 환율 변동 리스크와 대응 방안을 한 단락 추가해줘.',
      fileName: '노스윈드_2026_하반기_영업전략.docx',
      steps: [
        { title: '요청 이해하는 중', actor: 'RootAgent' },
        { title: '문서 변경 적용', actor: 'FastActionSelector → CanvasEditAgent', tools: ['CanvasEditAgent가 문서 구조 확인을 완료했습니다', 'CanvasEditAgent가 표 편집을 완료했습니다'] },
      ],
      doc: {
        title: '2026 하반기 영업 전략 (초안)',
        overview: ['1. 개요', '상반기 매출은 목표 대비 92%를 달성했으며, 기존 고객 재계약률은 94%로 양호했다.'],
        market: ['2. 시장 현황', ['중견기업을 중심으로 업무 자동화 수요 증가', '경쟁사 A는 가격 인하로 중소기업 시장 공략', '고객은 도입 속도와 사내 시스템 연동을 중시']],
        goals: ['3. 하반기 핵심 목표', '신규 고객 확보와 객단가 상승을 동시에 추진한다.'],
        table: { head: ['지표', '상반기 실적', '하반기 목표'], rows: [['신규 고객 수', '38개사', '60개사'], ['평균 계약 금액', '4,200만 원', '5,000만 원'], ['재계약률', '94%', '95% 이상']] },
        addedHead: '달성 방안', addedCells: ['제조 업종 웨비나·파트너 제안', '상위 요금제 전환 캠페인', '분기별 고객 성공 리뷰'],
        plan: ['4. 실행 계획', ['7월: 제조 업종 대상 웨비나 2회 개최', '8월: 파트너사 공동 제안 패키지 출시', '9월: 상위 요금제 전환 캠페인']],
        risk: ['5. 리스크 및 대응', '경기 둔화로 인한 예산 동결에 대비해 무료 파일럿과 단계별 도입안을 준비한다.'],
        addedRisk: '환율 변동으로 해외 솔루션 원가가 오를 수 있으므로 주요 계약은 원화 고정 단가로 체결하고, 분기마다 환율 민감도를 점검한다.',
      },
      reply: { lead: '요청하신 문서 수정 작업을 완료했습니다.', heading: '반영된 변경 사항', items: ['3장 목표 표에 "달성 방안" 열을 추가했습니다.', '5장에 환율 변동 리스크와 대응 방안을 추가했습니다.'], closing: '문서에서 변경된 내용을 확인해 주세요.' },
    },
    {
      id: 'data', tab: '데이터 분석',
      prompt: 'Q3 지역별 매출 파일에서 목표 대비 달성률을 차트로 보여주고, 미달 지역의 원인을 정리해줘.',
      attachment: '노스윈드_Q3_지역별_매출.xlsx',
      steps: [
        { title: '요청 이해하는 중', actor: 'RootAgent' },
        { title: '매출 데이터 분석', actor: 'FastActionSelector → ExcelAgent', tools: ['ExcelAgent가 시트 읽기를 완료했습니다'] },
        { title: '달성률 차트 생성', actor: 'ExcelAgent → DataScienceAgent', tools: ['DataScienceAgent가 차트 생성을 완료했습니다'] },
      ],
      chartTitle: 'Q3 지역별 목표 달성률', target: '목표 100%', series: [['서울', 112], ['경기', 104], ['부산', 91], ['대구', 86]],
      lead: '서울과 경기는 목표를 넘겼고, 부산과 대구는 목표에 미치지 못했습니다.',
      insights: ['두 지역 모두 8월 이후 문서 자동화 제품 매출이 정체', '파트너 채널 비중이 낮아 신규 유입이 적음', '4분기에는 파트너 공동 제안과 지역 웨비나를 우선 추진'],
      output: 'Q3_달성률_분석.xlsx',
    },
    {
      id: 'plugin', tab: '사내 도구 연동',
      prompt: 'Slack #영업팀 채널에서 이번 주 고객 이슈를 찾아 Jira 이슈로 등록해줘.',
      steps: [
        { title: '요청 이해하는 중', actor: 'RootAgent' },
        { title: 'Slack 대화 확인', actor: 'FastActionSelector → PluginAgent', tools: ['PluginAgent가 Slack 메시지 검색을 완료했습니다'] },
        { title: 'Jira 이슈 등록', actor: 'PluginAgent · Atlassian' },
      ],
      approval: { label: 'MCP 실행 권한', title: 'Jira에 이슈 3건을 생성합니다.', preview: '실행할 내용', fields: [['프로젝트', 'SALES'], ['유형', '작업'], ['제목', 'A사 전자서명 오류 외 2건']], run: '실행', deny: '실행하지 않음' },
      lead: 'Jira에 이슈 3건을 등록하고 Slack 원문 링크를 첨부했습니다.',
      issues: [['SALES-231', 'A사 계약서 전자서명 오류'], ['SALES-232', 'B사 월간 리포트 누락 문의'], ['SALES-233', 'C사 사용자 권한 추가 요청']],
    },
  ],
  intro: {
    eyebrow: 'askyour.ai 소개',
    lead1: 'askyour.ai는 agent orchestration layer 위에서 다양한 AI 기능을 통합하고 연결하는 구조로, 사용 목적에 따라 자연스럽게 이어지는 AI 경험을 제공합니다.',
    body1: ' 개인은 더 쉽게 활용하고, 기업은 더 유연하게 구축할 수 있어 일상 업무부터 엔터프라이즈 환경까지 폭넓게 대응할 수 있습니다.',
    lead2: '복잡한 에이전트 구축은 askyour.ai에게 맡기세요.',
    body2: ' 다중 에이전트 시스템은 자율적으로 동작하지만 공동의 목표를 달성하기 위해 사용자의 입력을 이해하고, 의사결정을 내리며, 작업을 수행하기 위해 협업하는 여러 AI 에이전트로 구성됩니다.',
  },
  capabilities: {
    eyebrow: '할 수 있는 일', title: ['반복되는 업무는', '에이전트에게 맡기세요.'], sub: '보고서 초안부터 데이터 정리, 발표 자료까지. 동료에게 부탁하듯 요청하면 됩니다.',
    cards: [
      { lead: '리서치·보고서', body: '웹과 문서를 찾아 읽고, 출처가 달린 보고서로 정리합니다.', prompt: '경쟁사 3곳의 요금 정책을 비교해줘' },
      { lead: '문서 초안', body: '회의록과 자료를 바탕으로 제안서·보고서 초안을 씁니다.', prompt: '회의록으로 제안서 초안을 써줘' },
      { lead: '데이터 분석', body: '엑셀 파일을 읽고 계산해 차트와 인사이트로 보여 줍니다.', prompt: '월별 매출 추이를 차트로 그려줘' },
      { lead: '발표 자료', body: '내용에 맞춰 슬라이드를 구성하고 디자인까지 점검합니다.', prompt: '분기 실적 보고 슬라이드 만들어줘' },
      { lead: '한글·PDF 문서', body: '한글(HWPX) 문서를 쓰고 고치며, PDF 내용을 추출합니다.', prompt: '협조 요청 공문을 한글 파일로 써줘' },
      { lead: '이미지 생성·분석', body: '홍보용 이미지를 만들고, 이미지 속 내용을 읽어 냅니다.', prompt: '신제품 프로모션 이미지 만들어줘' },
    ],
    mocks: {
      research: { heading: '경쟁사 요금 정책 비교', head: ['회사', '기본 요금', '최근 변화'], rows: [['A사', '월 9만 원', '사용량 과금 도입'], ['B사', '월 12만 원', '연간 할인 확대'], ['C사', '월 7만 원', '무료 체험 30일']], sources: '출처 6개' },
      draft: { file: '업무자동화_도입_제안서.docx', title: '업무 자동화 도입 제안서', lines: ['1. 제안 배경', '2. 도입 범위와 일정'], highlight: '도입 효과: 주간 보고서 작성 시간을 절반으로 줄입니다.' },
      chart: { title: '월별 매출 추이', months: ['7월', '8월', '9월', '10월', '11월', '12월'], file: '월별_매출.xlsx' },
      slides: ['3분기 실적 보고', '매출 요약', '지역별 성과', '다음 분기 계획'],
      hwp: { org: '노스윈드 코리아', to: '수신  각 부서장', subject: '제목  하반기 교육 일정 협조 요청', body: '하반기 직무 교육을 다음과 같이 운영하오니 부서별 참석 인원을 회신하여 주시기 바랍니다.' },
      image: 'promotion_banner.png',
    },
    rosterTitle: '12개의 전문 에이전트가 함께 일합니다.',
    rosterSub: '통합 에이전트가 요청을 이해하고 알맞은 전문 에이전트에게 일을 나눕니다. 빠른 실행, 계획 후 실행, 스킬 중 원하는 방식으로 진행할 수도 있어요.',
    roster: [
      ['웹 리서치', '검색·지도·논문까지 찾아 읽기'], ['Word 문서', '작성·편집·번역·분석'], ['Excel', '수식·차트·피벗이 있는 통합 문서'], ['PowerPoint', '브랜드에 맞춘 발표 자료'],
      ['한글 문서', 'HWPX 작성·편집, HWP 번역'], ['PDF', '텍스트·레이아웃 추출, Word 변환'], ['데이터 시각화', '차트·다이어그램·Mermaid'], ['코드 실행', '샌드박스에서 코드·명령 실행'],
      ['이미지', '이미지 생성·편집·인식'], ['멀티모달 분석', '이미지·음성·영상 이해'], ['프로젝트 위키', '사내 문서 기반 답변'], ['사내 도구', 'Slack·Jira·Gmail 등 연동 실행'],
    ],
  },
  docs: {
    eyebrow: '문서 에이전트', title: ['Word·Excel·PowerPoint를', '대화로 편집하세요.'],
    sub: '파일을 올리고 바꿀 내용을 말하면, 문서 에이전트가 원본 서식을 지키며 편집합니다. 편집기에서 바로 확인하고 다시 고칠 수 있어요.',
    points: [['원본 서식 그대로', '표·목록·스타일을 유지한 채 필요한 부분만 고칩니다.'], ['프로젝트로 모아 관리', '관련 문서를 프로젝트에 모아 두고 함께 참고해 작업합니다.'], ['출처가 남는 위키', '프로젝트 문서로 위키를 만들고, 원문 페이지 표시와 함께 핵심을 찾아봅니다.']],
    wiki: {
      original: '원본', wiki: '위키', project: '노스윈드 영업본부', refresh: '갱신', files: ['2026_하반기_영업전략.docx', '고객지원_운영정책.docx', 'Q3_지역별_매출.xlsx'],
      keyPoints: '핵심 포인트', concepts: ['가격 정책', '경쟁 기업', '성과 지표', '응답 시간 기준', '영업 전략'], edit: '편집',
      docTitle: '고객지원 운영 정책 v3.2',
      docSections: [['1. 목적', '고객 문의에 일관된 품질로 응대하기 위한 기준과 절차를 정의한다.'], ['2. 응답 시간 기준 (SLA)', '긴급(서비스 중단): 30분 이내 1차 응답, 4시간 이내 해결'], ['3. 에스컬레이션 절차', '1차 상담에서 해결되지 않는 문의는 기술지원팀으로 이관한다.'], ['4. 환불 및 보상', '서비스 중단이 월 누적 4시간을 넘으면 이용료의 10%를 크레딧으로 보상한다.']],
      summary: '고객 문의에 일관된 품질로 응대하기 위한 기준과 처리 절차를 정한 지침이다. 등급별 응답 시간, 에스컬레이션 절차, 보상 기준을 다룬다.',
      coreTitle: '핵심 내용',
      core: [['응답 시간 기준 (SLA)', '긴급 문의는 30분 이내 1차 응답, 4시간 이내 해결을 원칙으로 한다.'], ['에스컬레이션 절차', '1차 상담에서 해결되지 않은 문의는 기술지원팀으로 이관한다.'], ['환불 및 보상', '월 누적 중단이 4시간을 넘으면 이용료의 10%를 크레딧으로 보상한다.']],
    },
  },
  integrations: {
    eyebrow: '사내 도구 연동', title: ['쓰던 업무 도구와', '그대로 연결됩니다.'],
    sub: 'Slack, Notion, Jira·Confluence, Gmail, Google Calendar, Microsoft Teams, Outlook, GitHub까지. 필요한 서비스를 연결하면 에이전트가 대화 안에서 찾고, 정리하고, 실행합니다.',
    cards: [['여러 도구를 한 번에', '메신저에서 찾은 내용을 이슈 트래커와 문서에 바로 옮깁니다.'], ['실행 전에 확인', '메일 발송이나 이슈 생성처럼 변경이 생기는 작업은 승인 후에 실행합니다.'], ['필요한 도구만 켜기', '대화마다 사용할 서비스를 골라 켜고 끌 수 있습니다.']],
    flow: { from: '#영업팀', message: 'B사 월간 리포트가 이번 달엔 안 왔다고 하네요.', to: 'SALES-232', result: 'B사 월간 리포트 누락 문의' },
    approval: { label: 'MCP 실행 권한', title: 'Gmail로 메일을 보냅니다.', preview: '실행할 내용', fields: [['받는 사람', 'partner@northwind.example'], ['제목', '10월 공동 웨비나 일정 안내']], run: '실행', deny: '실행하지 않음' },
    toggles: { title: '플러그인 연결', items: [['Slack', true], ['Atlassian', true], ['Notion', true], ['Gmail', false], ['Google Calendar', false]] },
  },
  teams: {
    eyebrow: '팀과 함께', title: ['팀 전체가 쓰는', 'AI 워크스페이스.'],
    sub: '팀 워크스페이스를 만들고 동료를 초대하세요. 잘 만든 업무 방식은 스킬로 공유하고, 사용량은 한눈에 관리합니다.',
    cards: [['팀 워크스페이스', '멤버와 토큰·저장 공간을 팀 단위로 관리합니다.'], ['사내 스킬 공유', '반복 업무를 스킬로 만들어 회사 전체와 나눕니다.'], ['다이렉트 메시지·그룹 채팅', '동료와 대화하며 결과물을 바로 공유합니다.'], ['사용량 대시보드', '사용 횟수와 토큰, 진행 중인 작업을 확인합니다.']],
    team: { name: '노스윈드 영업본부', role: '관리자', members: '멤버', tokens: '남은 토큰', storage: '스토리지', usage: '토큰 잔여', created: '2026년 9월 1일 생성' },
    skills: { tabs: ['내 스킬', '레퍼런스 스킬', '공유 스킬'], items: [['주간 영업 보고서', '영업기획팀 · 공유 38회'], ['제안서 검토 체크리스트', '전략기획팀 · 공유 21회'], ['고객 미팅 요약', '고객성공팀 · 공유 17회']] },
    chat: { group: '영업본부 · 하반기 전략', messages: [['박서준', '3장 목표 표 업데이트된 버전 공유 부탁드려요.'], ['askyour.ai', '「2026 하반기 영업 전략」 최신본을 공유했습니다.'], ['김지현', '확인했어요. 오늘 회의 자료로 쓸게요.']] },
    dash: { title: '사용량 대시보드', stats: [['최근 30일 사용', '1,284회'], ['총 토큰', '18.6M'], ['평균 소요', '21.9초']], activity: '활동' },
    controls: [['초대 링크로 합류', '링크 하나로 팀원을 초대합니다.'], ['토큰·저장 공간 한도', '팀 단위로 사용량을 나눠 관리합니다.'], ['실행 전 승인', '변경 작업은 확인 후에만 실행됩니다.'], ['비밀번호 공유 링크', '대화를 공유할 때 비밀번호를 걸 수 있습니다.']],
  },
  everyday: {
    eyebrow: '매일의 업무 도구', title: ['에이전트 말고도,', '매일 쓰는 도구가 있습니다.'], sub: '기억하고, 말로 묻고, 번역하고, 업계 소식을 챙기는 일까지 하나의 워크스페이스에서.',
    cards: [['에이전트 메모리', '업무 맥락과 선호를 기억하고, 내용은 직접 확인·수정합니다.'], ['라이브 에이전트', '실시간 음성으로 묻고 답하며 검색과 도구를 함께 씁니다.'], ['번역', '비즈니스·학술 도메인과 용어 사전으로 문장과 문서를 번역합니다.'], ['피드', '관심 분야의 뉴스와 블로그, 영상을 매일 브리핑합니다.']],
    memory: { title: '제가 이해하고 있는 내용', items: [['업무 맥락', '하반기 영업 전략 문서를 주 1회 업데이트함'], ['일하는 방식', '보고서는 핵심 요약과 표를 먼저 선호']], evidence: '근거 4개 보기' },
    live: { status: '듣고 있어요', user: '오늘 오후 미팅 전에 B사 최근 이슈만 짧게 알려줘.', agent: '이번 주 B사 관련 이슈는 월간 리포트 누락 문의 1건입니다.' },
    translate: { styles: ['일반', '학술', '비즈니스'], source: '다음 분기 공동 제안 일정을 확정해 주세요.', target: 'Please confirm the schedule for next quarter’s joint proposal.', glossary: '용어 사전' },
    feed: { label: 'DAILY FEED', items: [['뉴스', '제조업 AI 도입, 문서 업무부터 빠르게 확산'], ['블로그', 'B2B 영업팀이 에이전트를 쓰는 5가지 방법']] },
    more: [['파일 저장소', '업로드하고 만든 모든 파일을 한곳에서'], ['위젯', '캘린더·메일·메모를 한 화면에'], ['도구 모음', '계산기·날짜·JSON 등 간편 도구'], ['대화 공유', '유용한 대화를 링크로 공유']],
  },
  cases: {
    eyebrow: '도입 사례', title: ['기업마다 다른 과제에', '맞춤형으로 답했습니다.'],
    sub: '보안이 엄격한 온프레미스 환경부터 사내 지식 체계화, ERP 연동 자동화까지. 각 기업의 정책과 업무 방식에 맞춰 askyour.ai를 구축한 사례입니다.',
    items: [
      { company: 'S사', kind: '온프레미스 구축', title: '데이터가 사내를 벗어나지 않는 AI 워크스페이스', body: '개인정보와 사내 문서처럼 보안이 중요한 데이터를 다루는 환경에 맞춰 askyour.ai를 온프레미스로 구축하고, 사내 정책에 맞게 커스터마이징했습니다. 모든 데이터는 사내 인프라 안에서 처리·보관되며 외부 LLM API로 전송되지 않습니다.', points: ['온프레미스 구축·커스터마이징', '사내 인프라 내 데이터 처리', '외부 LLM API 전송 차단'] },
      { company: 'P사', kind: '지식 온톨로지', title: '흩어진 암묵지를 에이전트가 활용하는 지식으로', body: '부서와 문서 곳곳에 흩어져 있던 내부 지식과 암묵지를 온톨로지로 구조화했습니다. 이를 탑재한 에이전트 프레임워크를 제공해, 조직에 축적된 노하우를 업무 현장에서 바로 활용할 수 있도록 했습니다.', points: ['내부 문서·암묵지 통합', '온톨로지 기반 지식 구조화', '사내 지식 탑재 에이전트 프레임워크'] },
      { company: 'K사', kind: 'ERP 연동 자동화', title: '수기 전표 처리를 에이전트 파이프라인으로', body: 'ERP에 전표를 직접 작성하던 반복 업무를 자동화했습니다. askyour.ai와 ERP를 연동해 에이전트가 전표 처리에 필요한 정보를 추출하고, ERP 입력까지 이어지는 전 과정을 하나의 파이프라인으로 처리합니다.', points: ['askyour.ai–ERP 연동', '전표 정보 자동 추출', '추출부터 입력까지 파이프라인 자동화'] },
    ],
    onprem: { title: '배포 환경', badge: 'On-premises', rows: [['LLM', '사내 모델'], ['문서·데이터', '사내 스토리지']], blocked: ['외부 LLM API', '차단'] },
    ontology: { title: '사내 지식 온톨로지', docs: ['설비 점검 매뉴얼.pdf', '작업 표준서.hwpx', '회의록 모음.docx'], concepts: ['설비', '공정', '품질 기준'], agent: '에이전트에 탑재됨' },
    erp: { title: '전표 처리 파이프라인', extract: '전표 정보 추출', fields: [['거래처', '(주)노스윈드'], ['금액', '₩1,250,000'], ['계정과목', '소모품비']], done: 'ERP 입력 완료', voucher: '전표 2026-0142' },
  },
  faq: {
    eyebrow: '자주 묻는 질문', title: '도입 전에 궁금한 점',
    items: [
      { q: '팀 단위로 도입할 수 있나요?', a: '팀 워크스페이스를 만들고 초대 링크로 동료를 초대할 수 있어요. 팀 관리 화면에서 멤버와 토큰, 저장 공간을 확인하고 관리합니다. 조직 도입은 도입 문의로 연락해 주세요.' },
      { q: '어떤 파일을 다룰 수 있나요?', a: '문서 에이전트에서 Word(DOCX), Excel(XLSX), PowerPoint(PPTX)를 편집하고 PDF를 열람할 수 있어요. 통합 에이전트는 한글(HWPX) 문서 작성·편집과 PDF 텍스트 추출, 이미지 분석도 지원합니다.' },
      { q: '어떤 업무 도구와 연결되나요?', a: 'Slack, Notion, Jira·Confluence(Atlassian), Gmail, Google Calendar, Microsoft Teams, Outlook 메일·캘린더, GitHub, Discord를 플러그인으로 연결할 수 있어요. Google Drive와 OneDrive 파일은 대화에 바로 첨부할 수 있습니다.' },
      { q: '에이전트가 마음대로 작업을 실행하지 않나요?', a: '메일 발송이나 이슈 생성처럼 변경이 생기는 작업은 기본적으로 실행 전에 승인을 요청합니다. 실행할 내용을 확인한 뒤 실행하거나 거절할 수 있어요.' },
      { q: '요금은 어떻게 되나요?', a: 'Google 계정으로 가입하면 무료 요금제로 바로 시작할 수 있어요. 더 많은 사용량이 필요하면 요금제 페이지에서 플랜을 비교해 보세요.' },
    ],
  },
  closing: { title: ['여러분의 다음 업무,', 'Askyour.ai와 함께하세요.'], sub: '무료로 시작하고, 엔터프라이즈 도입은 언제든 문의하세요.' },
  footer: { tagline: '일을 끝내는 AI 에이전트 워크스페이스', product: '제품', company: '문의', legal: '약관', terms: '이용약관', privacy: '개인정보처리방침', email: '이메일 문의' },
};

const en: LandingCopy = {
  nav: { capabilities: 'Capabilities', cases: 'Case studies', pricing: 'Pricing', menu: 'Menu' },
  login: 'Get started with Google', signIn: 'Get started', loading: 'Opening sign in', loginError: 'Could not start sign in. Please try again.', contact: 'Talk to sales', language: 'Choose language', skip: 'Skip to content',
  hero: {
    title: 'Meet your own AI agent',
    lead: 'One screen, one flow.',
    sub: 'askyour.ai is the all-in-one AI workspace that automates repetitive work. From on-premises deployment fitted to your environment to custom AI built for your business, we support you flexibly.',
  },
  demo: { label: 'Product demo', play: 'Play demo', pause: 'Pause demo', replay: 'Replay from the start', url: 'askyour.ai' },
  ui: {
    personal: 'Personal', team: 'Team', workspace: 'Workspace', teamWorkspace: 'Team workspace', personalSection: 'Personal', manage: 'Management',
    agent: 'Integrated Agent', live: 'Live Agent', docAgent: 'Document Agent', teamManage: 'Team Management', widget: 'Widgets', feed: 'Feed', tools: 'Toolbox',
    dm: 'Direct Message', storage: 'File Storage', plugin: 'Plugins', memory: 'Agent Memory', profile: 'Jamie Kim', profileSub: 'Northwind Sales',
    placeholder: 'Type a message…', disclaimer: 'Agent can make mistakes. Please verify important information.', welcome: 'Your personal agent for everyday tasks', steps: '{n} steps', done: 'Done', saved: 'Saved', export: 'Export',
    menus: ['File', 'Format', 'Insert'], fit: 'Fit page', paragraph: 'Normal text',
  },
  scenes: [
    {
      id: 'research', tab: 'Research & reports',
      prompt: 'Summarize the top three B2B SaaS trends for 2026 with sources, and finish with a summary table.',
      steps: [
        { title: 'Understanding the request', actor: 'RootAgent' },
        { title: 'Choosing how to run it', actor: 'RootClassifier → FastActionSelector' },
        { title: 'Researching B2B SaaS market trends', actor: 'FastActionSelector → WebAgent', body: 'Searching industry reports, articles and public data to identify the three key trends.', tools: ['WebAgent finished using the web search tool', 'WebAgent finished using the page reader tool'] },
        { title: 'Writing the report', actor: 'WebAgent → BasicAgent' },
      ],
      lead: 'In 2026, growth in B2B SaaS is being driven by AI agents and new pricing models. Here are the three trends that matter most.',
      heading: 'Key trends at a glance',
      table: { head: ['Trend', 'What’s changing', 'What buyers look for'], rows: [['AI agents built in', 'Assistance → doing the work', 'Connects to internal systems'], ['Vertical SaaS', 'Industry-specific workflows', 'Fast rollout and ROI'], ['New pricing models', 'Per seat → usage and outcomes', 'Predictable budgets']] },
      sources: '8 sources',
    },
    {
      id: 'document', tab: 'Document editing',
      prompt: 'Add an “Action plan” column to the goals table in section 3, and add a paragraph on currency risk to section 5.',
      fileName: 'Northwind_H2_2026_Sales_Strategy.docx',
      steps: [
        { title: 'Understanding the request', actor: 'RootAgent' },
        { title: 'Applying document changes', actor: 'FastActionSelector → CanvasEditAgent', tools: ['CanvasEditAgent finished reading the document structure', 'CanvasEditAgent finished editing the table'] },
      ],
      doc: {
        title: 'H2 2026 Sales Strategy (Draft)',
        overview: ['1. Overview', 'We reached 92% of our first-half revenue target, and renewals among existing customers held strong at 94%.'],
        market: ['2. Market', ['Demand for workflow automation is growing among mid-sized companies', 'Competitor A is cutting prices to win small businesses', 'Customers prioritize fast rollout and system integration']],
        goals: ['3. Goals for H2', 'Grow new customers and average deal size at the same time.'],
        table: { head: ['Metric', 'H1 result', 'H2 target'], rows: [['New customers', '38', '60'], ['Average deal size', '$32K', '$38K'], ['Renewal rate', '94%', '95%+']] },
        addedHead: 'Action plan', addedCells: ['Manufacturing webinars, partner offers', 'Upgrade campaign for top plans', 'Quarterly customer success reviews'],
        plan: ['4. Execution plan', ['July: two webinars for manufacturing', 'August: joint offer package with partners', 'September: plan upgrade campaign']],
        risk: ['5. Risks', 'Prepare free pilots and phased rollouts in case budgets freeze during a slowdown.'],
        addedRisk: 'Exchange-rate swings may raise the cost of overseas services, so sign key contracts at fixed local-currency prices and review currency sensitivity every quarter.',
      },
      reply: { lead: 'I’ve finished editing the document.', heading: 'Changes made', items: ['Added an “Action plan” column to the goals table in section 3.', 'Added a paragraph on currency risk and responses to section 5.'], closing: 'Review the changes in the document.' },
    },
    {
      id: 'data', tab: 'Data analysis',
      prompt: 'Chart attainment against target by region from the Q3 sales file, and explain why some regions fell short.',
      attachment: 'Northwind_Q3_Sales_by_Region.xlsx',
      steps: [
        { title: 'Understanding the request', actor: 'RootAgent' },
        { title: 'Analyzing sales data', actor: 'FastActionSelector → ExcelAgent', tools: ['ExcelAgent finished reading the sheet'] },
        { title: 'Creating the attainment chart', actor: 'ExcelAgent → DataScienceAgent', tools: ['DataScienceAgent finished creating the chart'] },
      ],
      chartTitle: 'Q3 target attainment by region', target: 'Target 100%', series: [['West', 112], ['East', 104], ['South', 91], ['Central', 86]],
      lead: 'West and East beat their targets, while South and Central came in below.',
      insights: ['Both regions stalled on document automation after August', 'Few partner channels, so fewer new leads', 'In Q4, prioritize joint partner offers and regional webinars'],
      output: 'Q3_attainment_analysis.xlsx',
    },
    {
      id: 'plugin', tab: 'Connected tools',
      prompt: 'Find this week’s customer issues in the #sales Slack channel and file them as Jira issues.',
      steps: [
        { title: 'Understanding the request', actor: 'RootAgent' },
        { title: 'Reading Slack conversations', actor: 'FastActionSelector → PluginAgent', tools: ['PluginAgent finished searching Slack messages'] },
        { title: 'Filing Jira issues', actor: 'PluginAgent · Atlassian' },
      ],
      approval: { label: 'MCP permission', title: 'Create 3 issues in Jira.', preview: 'What will run', fields: [['Project', 'SALES'], ['Type', 'Task'], ['Summary', 'E-signature error at Acme + 2 more']], run: 'Run', deny: 'Don’t run' },
      lead: 'I filed 3 issues in Jira and linked each one to the original Slack thread.',
      issues: [['SALES-231', 'E-signature error on Acme contract'], ['SALES-232', 'Globex missing monthly report'], ['SALES-233', 'Initech requests extra user seats']],
    },
  ],
  intro: {
    eyebrow: 'Introducing askyour.ai',
    lead1: 'askyour.ai brings a range of AI capabilities together on an agent orchestration layer, so every task flows naturally into the next, whatever you use it for.',
    body1: ' Individuals get more done with less effort, and companies can build on it flexibly, from everyday work to enterprise environments.',
    lead2: 'Leave the hard work of building agents to askyour.ai.',
    body2: ' A multi-agent system is made up of AI agents that act on their own yet work together toward a shared goal: they understand what you ask, make decisions and carry out the work.',
  },
  capabilities: {
    eyebrow: 'What it can do', title: ['Hand off the repetitive work', 'to your agents.'], sub: 'From report drafts to data cleanup and slide decks. Just ask the way you’d ask a colleague.',
    cards: [
      { lead: 'Research and reports', body: 'Finds and reads sources on the web and in your files, then writes a sourced report.', prompt: 'Compare pricing at our top 3 competitors' },
      { lead: 'First drafts', body: 'Turns meeting notes and materials into proposals and reports.', prompt: 'Draft a proposal from these meeting notes' },
      { lead: 'Data analysis', body: 'Reads and calculates across spreadsheets, then shows charts and insights.', prompt: 'Chart our monthly revenue trend' },
      { lead: 'Slide decks', body: 'Structures slides around your content and reviews the design.', prompt: 'Build a quarterly results deck' },
      { lead: 'HWP and PDF', body: 'Writes and edits Korean HWPX documents and extracts content from PDFs.', prompt: 'Write a request letter as an HWPX file' },
      { lead: 'Images', body: 'Creates promotional images and reads what’s inside images.', prompt: 'Create a launch promo image' },
    ],
    mocks: {
      research: { heading: 'Competitor pricing', head: ['Company', 'Base price', 'Recent change'], rows: [['Acme', '$69/mo', 'Added usage pricing'], ['Globex', '$89/mo', 'Bigger annual discount'], ['Initech', '$49/mo', '30-day free trial']], sources: '6 sources' },
      draft: { file: 'Automation_Proposal.docx', title: 'Workflow Automation Proposal', lines: ['1. Background', '2. Scope and timeline'], highlight: 'Impact: cut weekly reporting time in half.' },
      chart: { title: 'Monthly revenue', months: ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], file: 'Monthly_revenue.xlsx' },
      slides: ['Q3 results', 'Revenue summary', 'Regional performance', 'Next quarter'],
      hwp: { org: 'Northwind Korea', to: 'To  All department heads', subject: 'Re  H2 training schedule', body: 'H2 training will run as follows. Please reply with the number of attendees from each department.' },
      image: 'promotion_banner.png',
    },
    rosterTitle: '12 specialist agents, working together.',
    rosterSub: 'The integrated agent understands your request and hands each part to the right specialist. You can also choose fast, plan-first or skill-based runs.',
    roster: [
      ['Web research', 'Search, maps and papers'], ['Word', 'Write, edit, translate, analyze'], ['Excel', 'Workbooks with formulas, charts, pivots'], ['PowerPoint', 'On-brand slide decks'],
      ['HWP documents', 'Create and edit HWPX, translate HWP'], ['PDF', 'Extract text and layout, convert to Word'], ['Data visualization', 'Charts, diagrams, Mermaid'], ['Code execution', 'Run code and commands in a sandbox'],
      ['Images', 'Generate, edit and recognize'], ['Multimodal', 'Understand images, audio and video'], ['Project wiki', 'Answers grounded in your docs'], ['Work tools', 'Act in Slack, Jira, Gmail and more'],
    ],
  },
  docs: {
    eyebrow: 'Document agent', title: ['Edit Word, Excel and PowerPoint', 'by simply asking.'],
    sub: 'Upload a file and describe the change. The document agent edits it while keeping the original formatting, and you can review and refine it right in the editor.',
    points: [['Formatting stays intact', 'Tables, lists and styles stay as they are. Only what needs to change does.'], ['Organized by project', 'Keep related files in one project and work across them together.'], ['A wiki with sources', 'Build a wiki from project files and trace every point back to the original.']],
    wiki: {
      original: 'Originals', wiki: 'Wiki', project: 'Northwind Sales', refresh: 'Refresh', files: ['H2_2026_Sales_Strategy.docx', 'Support_Policy.docx', 'Q3_Sales_by_Region.xlsx'],
      keyPoints: 'Key points', concepts: ['Pricing policy', 'Competitors', 'Performance metrics', 'Response times', 'Sales strategy'], edit: 'Edit',
      docTitle: 'Customer Support Policy v3.2',
      docSections: [['1. Purpose', 'Defines the standards and process for responding to customers with consistent quality.'], ['2. Response times (SLA)', 'Urgent (outage): first response in 30 min, resolved within 4 hours'], ['3. Escalation', 'Requests not resolved at first contact move to the technical support team.'], ['4. Refunds and credits', 'If outages exceed 4 hours in a month, 10% of that month’s fee is credited.']],
      summary: 'Guidelines for handling customer requests with consistent quality, covering response times by priority, the escalation process and compensation rules.',
      coreTitle: 'Key facts',
      core: [['Response times (SLA)', 'Urgent requests get a first response within 30 minutes and are resolved within 4 hours.'], ['Escalation', 'Anything unresolved at first contact moves to technical support.'], ['Refunds and credits', 'Outages over 4 hours a month earn a 10% credit on that month’s fee.']],
    },
  },
  integrations: {
    eyebrow: 'Integrations', title: ['Works with the tools', 'your team already uses.'],
    sub: 'Slack, Notion, Jira and Confluence, Gmail, Google Calendar, Microsoft Teams, Outlook and GitHub. Connect what you need, and the agent finds, organizes and acts on it within the conversation.',
    cards: [['Many tools, one request', 'Move what’s said in chat straight into your issue tracker and docs.'], ['Review before it runs', 'Actions that change things, like sending email or creating issues, wait for your approval.'], ['Only the tools you need', 'Turn services on or off for each conversation.']],
    flow: { from: '#sales', message: 'Globex says their monthly report didn’t arrive this month.', to: 'SALES-232', result: 'Globex missing monthly report' },
    approval: { label: 'MCP permission', title: 'Send an email with Gmail.', preview: 'What will run', fields: [['To', 'partner@northwind.example'], ['Subject', 'Joint webinar schedule for October']], run: 'Run', deny: 'Don’t run' },
    toggles: { title: 'Plugin connections', items: [['Slack', true], ['Atlassian', true], ['Notion', true], ['Gmail', false], ['Google Calendar', false]] },
  },
  teams: {
    eyebrow: 'For teams', title: ['One AI workspace', 'for the whole team.'],
    sub: 'Create a team workspace and invite your colleagues. Share what works as skills, and keep usage in view.',
    cards: [['Team workspace', 'Manage members, tokens and storage by team.'], ['Shared company skills', 'Turn repeat work into skills and share them company-wide.'], ['Direct messages and group chat', 'Talk with teammates and share results on the spot.'], ['Usage dashboard', 'See runs, tokens and work in progress.']],
    team: { name: 'Northwind Sales', role: 'Admin', members: 'Members', tokens: 'Tokens left', storage: 'Storage', usage: 'Tokens remaining', created: 'Created Sep 1, 2026' },
    skills: { tabs: ['My skills', 'Reference skills', 'Shared skills'], items: [['Weekly sales report', 'Sales Ops · shared 38 times'], ['Proposal review checklist', 'Strategy · shared 21 times'], ['Customer meeting summary', 'Customer Success · shared 17 times']] },
    chat: { group: 'Sales · H2 strategy', messages: [['Alex Park', 'Could you share the latest version of the section 3 goals table?'], ['askyour.ai', 'I’ve shared the latest “H2 2026 Sales Strategy.”'], ['Jamie Kim', 'Got it, I’ll use it for today’s meeting.']] },
    dash: { title: 'Usage dashboard', stats: [['Runs (30 days)', '1,284'], ['Total tokens', '18.6M'], ['Avg. time', '21.9s']], activity: 'Activity' },
    controls: [['Invite links', 'Bring teammates in with a single link.'], ['Token and storage limits', 'Allocate usage by team.'], ['Approval before actions', 'Changes run only after you confirm.'], ['Password-protected sharing', 'Add a password when you share a conversation.']],
  },
  everyday: {
    eyebrow: 'Everyday tools', title: ['Beyond agents,', 'the tools you use every day.'], sub: 'Remember context, ask out loud, translate and keep up with your industry, all in one workspace.',
    cards: [['Agent memory', 'Remembers your work context and preferences. You can review and edit it.'], ['Live agent', 'Talk in real time, with search and tools along the way.'], ['Translation', 'Translate sentences and documents with business and academic domains and a glossary.'], ['Feed', 'A daily briefing of news, blogs and videos in your field.']],
    memory: { title: 'What I understand', items: [['Work context', 'Updates the H2 sales strategy doc every week'], ['How you work', 'Prefers reports that lead with a summary and a table']], evidence: 'See 4 sources' },
    live: { status: 'Listening', user: 'Before this afternoon’s meeting, give me a quick update on Globex.', agent: 'This week there’s one open Globex issue: a missing monthly report.' },
    translate: { styles: ['General', 'Academic', 'Business'], source: '다음 분기 공동 제안 일정을 확정해 주세요.', target: 'Please confirm the schedule for next quarter’s joint proposal.', glossary: 'Glossary' },
    feed: { label: 'DAILY FEED', items: [['News', 'Manufacturers adopt AI, starting with paperwork'], ['Blog', '5 ways B2B sales teams use agents']] },
    more: [['File storage', 'Every file you upload or create, in one place'], ['Widgets', 'Calendar, mail and notes on one screen'], ['Toolbox', 'Calculator, dates, JSON and more'], ['Sharing', 'Share useful conversations with a link']],
  },
  cases: {
    eyebrow: 'Case studies', title: ['Different challenges,', 'tailored deployments.'],
    sub: 'From tightly secured on-premises environments to structured company knowledge and ERP automation, here is how organizations built askyour.ai around their own policies and ways of working.',
    items: [
      { company: 'Company S', kind: 'On-premises deployment', title: 'An AI workspace where data never leaves the company', body: 'For an environment handling sensitive data such as personal information and internal documents, askyour.ai was deployed on premises and customized to company policy. All data is processed and stored within the company’s own infrastructure, and nothing is sent to external LLM APIs.', points: ['On-premises deployment and customization', 'Data processed in-house', 'Nothing sent to external LLM APIs'] },
      { company: 'Company P', kind: 'Knowledge ontology', title: 'Scattered tacit knowledge, turned into knowledge agents can use', body: 'Internal knowledge and tacit know-how scattered across teams and documents was structured into an ontology. An agent framework built on that knowledge puts the organization’s accumulated expertise to work where the work happens.', points: ['Internal documents and tacit knowledge unified', 'Ontology-based knowledge structure', 'Agent framework built on company knowledge'] },
      { company: 'Company K', kind: 'ERP automation', title: 'From manual journal entries to an agent pipeline', body: 'Repetitive manual journal entry work in the ERP was automated. With askyour.ai connected to the ERP, an agent extracts the details each entry needs and carries them all the way into the ERP in a single pipeline.', points: ['askyour.ai–ERP integration', 'Entry details extracted automatically', 'One pipeline from extraction to posting'] },
    ],
    onprem: { title: 'Deployment', badge: 'On-premises', rows: [['LLM', 'In-house model'], ['Documents & data', 'In-house storage']], blocked: ['External LLM API', 'Blocked'] },
    ontology: { title: 'Company knowledge ontology', docs: ['Equipment manual.pdf', 'Work standards.hwpx', 'Meeting notes.docx'], concepts: ['Equipment', 'Process', 'Quality'], agent: 'Loaded into agents' },
    erp: { title: 'Journal entry pipeline', extract: 'Entry details extracted', fields: [['Vendor', 'Northwind Inc.'], ['Amount', '$1,250.00'], ['Account', 'Supplies']], done: 'Posted to ERP', voucher: 'Entry 2026-0142' },
  },
  faq: {
    eyebrow: 'FAQ', title: 'Questions before you roll it out',
    items: [
      { q: 'Can we use it as a team?', a: 'Create a team workspace and invite colleagues with an invite link. The team management page shows members, tokens and storage. For company-wide rollouts, talk to sales.' },
      { q: 'Which files does it work with?', a: 'The document agent edits Word (DOCX), Excel (XLSX) and PowerPoint (PPTX) files and opens PDFs. The integrated agent also writes and edits HWPX documents, extracts text from PDFs and analyzes images.' },
      { q: 'Which work tools does it connect to?', a: 'Slack, Notion, Jira and Confluence (Atlassian), Gmail, Google Calendar, Microsoft Teams, Outlook mail and calendar, GitHub and Discord connect as plugins. You can also attach files from Google Drive and OneDrive.' },
      { q: 'Will the agent take actions on its own?', a: 'By default, actions that change something, such as sending email or creating issues, ask for approval first. You see exactly what will run and can approve or decline it.' },
      { q: 'How much does it cost?', a: 'Sign up with Google and start on the free plan right away. If you need more usage, compare plans on the pricing page.' },
    ],
  },
  closing: { title: ['Start your next task', 'with Askyour.ai.'], sub: 'Start free, and talk to us about enterprise deployment anytime.' },
  footer: { tagline: 'The AI agent workspace that finishes the work', product: 'Product', company: 'Contact', legal: 'Legal', terms: 'Terms of Service', privacy: 'Privacy Policy', email: 'Email us' },
};

const ja: LandingCopy = {
  ...en,
  nav: { capabilities: '機能', cases: '導入事例', pricing: '料金', menu: 'メニュー' },
  login: 'Googleではじめる', signIn: 'はじめる', loading: 'ログイン画面へ移動中', loginError: 'ログインを開始できませんでした。もう一度お試しください。', contact: '導入のご相談', language: '言語を選択', skip: '本文へスキップ',
  hero: {
    title: 'Meet your own AI agent',
    lead: 'ひとつの画面で、ひとつの流れで。',
    sub: 'askyour.aiは、繰り返しの業務を自動化するオールインワンのAIワークスペースです。社内環境に合わせたオンプレミス導入から企業向けのカスタムAI開発まで、柔軟にサポートします。',
  },
  demo: { label: '製品デモ', play: 'デモを再生', pause: 'デモを一時停止', replay: '最初から再生', url: 'askyour.ai' },
  ui: {
    personal: '個人', team: 'チーム', workspace: 'ワークスペース', teamWorkspace: 'チームワークスペース', personalSection: '個人', manage: '管理',
    agent: '統合エージェント', live: 'ライブエージェント', docAgent: 'ドキュメントエージェント', teamManage: 'チーム管理', widget: 'ウィジェット', feed: 'フィード', tools: 'ツールボックス',
    dm: 'ダイレクトメッセージ', storage: 'ファイルストレージ', plugin: 'プラグイン', memory: 'エージェントメモリ', profile: '佐藤 美咲', profileSub: 'ノースウィンド営業本部',
    placeholder: 'メッセージを入力…', disclaimer: 'Agentは誤りがある場合があります。重要な情報はご自身でご確認ください。', welcome: 'リアルタイムでエージェントと会話しましょう', steps: '{n}ステップ', done: '完了', saved: '保存済み', export: 'エクスポート',
    menus: ['ファイル', '書式', '挿入'], fit: 'ページに合わせる', paragraph: '標準テキスト',
  },
  scenes: [
    {
      ...en.scenes[0], tab: 'リサーチ・レポート',
      prompt: '2026年のB2B SaaS市場の主要トレンドを3つ、出典付きでまとめて、最後に表で要約して。',
      steps: [
        { title: '依頼を理解しています', actor: 'RootAgent' },
        { title: '実行方法を決定', actor: 'RootClassifier → FastActionSelector' },
        { title: 'B2B SaaS市場のトレンド調査', actor: 'FastActionSelector → WebAgent', body: '業界レポートや記事、公開資料を検索して主要トレンドを3つ抽出します。', tools: ['WebAgentがWeb検索ツールの使用を完了しました', 'WebAgentがページ読み取りツールの使用を完了しました'] },
        { title: 'レポート作成', actor: 'WebAgent → BasicAgent' },
      ],
      lead: '2026年のB2B SaaS市場は、AIエージェントの導入と課金モデルの変化が成長を牽引しています。主要なトレンドを3つにまとめました。',
      heading: '主要トレンドの要約',
      table: { head: ['トレンド', '主な変化', '導入のポイント'], rows: [['AIエージェントとの統合', '補助機能 → 業務の自動実行', '社内システム連携'], ['バーティカルSaaSの拡大', '業種・職種特化のワークフロー', '導入スピードとROI'], ['課金モデルの転換', 'シート課金 → 利用量・成果ベース', '予算の予測しやすさ']] },
      sources: '出典 8件',
    },
    {
      ...en.scenes[1], tab: '文書編集',
      prompt: '3章の目標表に「達成施策」の列を追加して、5章に為替変動リスクと対応策を1段落追加して。',
      fileName: 'ノースウィンド_2026下期_営業戦略.docx',
      steps: [
        { title: '依頼を理解しています', actor: 'RootAgent' },
        { title: '文書の変更を適用', actor: 'FastActionSelector → CanvasEditAgent', tools: ['CanvasEditAgentが文書構造の確認を完了しました', 'CanvasEditAgentが表の編集を完了しました'] },
      ],
      doc: {
        title: '2026年下期 営業戦略（案）',
        overview: ['1. 概要', '上期の売上は目標の92%を達成し、既存顧客の契約更新率は94%と堅調だった。'],
        market: ['2. 市場動向', ['中堅企業を中心に業務自動化の需要が増加', '競合Aは値下げで中小企業市場を攻略', '顧客は導入スピードと社内システム連携を重視']],
        goals: ['3. 下期の重点目標', '新規顧客の獲得と顧客単価の向上を同時に進める。'],
        table: { head: ['指標', '上期実績', '下期目標'], rows: [['新規顧客数', '38社', '60社'], ['平均契約額', '420万円', '500万円'], ['契約更新率', '94%', '95%以上']] },
        addedHead: '達成施策', addedCells: ['製造業向けウェビナー・パートナー提案', '上位プラン移行キャンペーン', '四半期ごとのカスタマーサクセスレビュー'],
        plan: ['4. 実行計画', ['7月：製造業向けウェビナーを2回開催', '8月：パートナー共同提案パッケージを発売', '9月：上位プラン移行キャンペーン']],
        risk: ['5. リスクと対応', '景気減速による予算凍結に備え、無料パイロットと段階的な導入案を用意する。'],
        addedRisk: '為替変動で海外ソリューションの原価が上がる可能性があるため、主要契約は円建ての固定単価で締結し、四半期ごとに為替感応度を点検する。',
      },
      reply: { lead: 'ご依頼の文書編集が完了しました。', heading: '反映した変更', items: ['3章の目標表に「達成施策」の列を追加しました。', '5章に為替変動リスクと対応策を追加しました。'], closing: '文書で変更内容をご確認ください。' },
    },
    {
      ...en.scenes[2], tab: 'データ分析',
      prompt: 'Q3の地域別売上ファイルから目標達成率をグラフにして、未達の地域の原因をまとめて。',
      attachment: 'ノースウィンド_Q3_地域別売上.xlsx',
      steps: [
        { title: '依頼を理解しています', actor: 'RootAgent' },
        { title: '売上データの分析', actor: 'FastActionSelector → ExcelAgent', tools: ['ExcelAgentがシートの読み取りを完了しました'] },
        { title: '達成率グラフの作成', actor: 'ExcelAgent → DataScienceAgent', tools: ['DataScienceAgentがグラフの作成を完了しました'] },
      ],
      chartTitle: 'Q3 地域別の目標達成率', target: '目標 100%', series: [['東京', 112], ['大阪', 104], ['名古屋', 91], ['福岡', 86]],
      lead: '東京と大阪は目標を上回り、名古屋と福岡は目標に届きませんでした。',
      insights: ['両地域とも8月以降、文書自動化製品の売上が停滞', 'パートナー経由の比率が低く、新規流入が少ない', '第4四半期はパートナー共同提案と地域ウェビナーを優先'],
      output: 'Q3_達成率分析.xlsx',
    },
    {
      ...en.scenes[3], tab: '社内ツール連携',
      prompt: 'Slackの#営業チャンネルから今週の顧客課題を探して、Jiraの課題として登録して。',
      steps: [
        { title: '依頼を理解しています', actor: 'RootAgent' },
        { title: 'Slackの会話を確認', actor: 'FastActionSelector → PluginAgent', tools: ['PluginAgentがSlackメッセージの検索を完了しました'] },
        { title: 'Jiraに課題を登録', actor: 'PluginAgent · Atlassian' },
      ],
      approval: { label: 'MCP実行権限', title: 'Jiraに課題を3件作成します。', preview: '実行する内容', fields: [['プロジェクト', 'SALES'], ['タイプ', 'タスク'], ['要約', 'A社 電子署名エラー ほか2件']], run: '実行', deny: '実行しない' },
      lead: 'Jiraに課題を3件登録し、Slackの元スレッドのリンクを添付しました。',
      issues: [['SALES-231', 'A社 契約書の電子署名エラー'], ['SALES-232', 'B社 月次レポート未着の問い合わせ'], ['SALES-233', 'C社 ユーザー権限の追加依頼']],
    },
  ],
  intro: {
    eyebrow: 'askyour.aiについて',
    lead1: 'askyour.aiは、agent orchestration layerの上でさまざまなAI機能を統合・連携する構造で、目的に応じて自然につながるAI体験を提供します。',
    body1: ' 個人はより手軽に活用でき、企業はより柔軟に構築できるため、日常業務からエンタープライズ環境まで幅広く対応できます。',
    lead2: '複雑なエージェント構築は、askyour.aiにお任せください。',
    body2: ' マルチエージェントシステムは、それぞれが自律的に動作しながらも、共通の目標を達成するためにユーザーの入力を理解し、意思決定を行い、協力して作業を進める複数のAIエージェントで構成されます。',
  },
  capabilities: {
    ...en.capabilities,
    eyebrow: 'できること', title: ['繰り返しの業務は、', 'エージェントに任せましょう。'], sub: 'レポートの下書きからデータ整理、プレゼン資料まで。同僚に頼むように依頼するだけです。',
    cards: [
      { lead: 'リサーチ・レポート', body: 'Webや文書を探して読み、出典付きのレポートにまとめます。', prompt: '競合3社の料金体系を比較して' },
      { lead: '文書の下書き', body: '議事録や資料をもとに、提案書やレポートの下書きを書きます。', prompt: '議事録から提案書の下書きを書いて' },
      { lead: 'データ分析', body: 'Excelファイルを読み取り計算し、グラフとインサイトで示します。', prompt: '月別売上の推移をグラフにして' },
      { lead: 'プレゼン資料', body: '内容に合わせてスライドを構成し、デザインまで点検します。', prompt: '四半期の業績報告スライドを作って' },
      { lead: 'HWP・PDF文書', body: '韓国のHWPX文書を作成・編集し、PDFから内容を抽出します。', prompt: '協力依頼の公文書をHWPXで作って' },
      { lead: '画像の生成・分析', body: '販促用の画像を作り、画像の内容を読み取ります。', prompt: '新製品のプロモーション画像を作って' },
    ],
    mocks: {
      research: { heading: '競合の料金体系の比較', head: ['会社', '基本料金', '最近の変化'], rows: [['A社', '月額9,900円', '従量課金を導入'], ['B社', '月額12,000円', '年間割引を拡大'], ['C社', '月額7,700円', '30日間無料トライアル']], sources: '出典 6件' },
      draft: { file: '業務自動化_導入提案書.docx', title: '業務自動化 導入提案書', lines: ['1. 提案の背景', '2. 導入範囲とスケジュール'], highlight: '導入効果：週次レポートの作成時間を半分に短縮します。' },
      chart: { title: '月別売上の推移', months: ['7月', '8月', '9月', '10月', '11月', '12月'], file: '月別売上.xlsx' },
      slides: ['第3四半期 業績報告', '売上サマリー', '地域別の成果', '来期の計画'],
      hwp: { org: 'ノースウィンド・コリア', to: '宛先  各部署長', subject: '件名  下期研修日程のご協力のお願い', body: '下期の職務研修を下記のとおり実施しますので、部署ごとの参加人数をご返信ください。' },
      image: 'promotion_banner.png',
    },
    rosterTitle: '12の専門エージェントが連携して働きます。',
    rosterSub: '統合エージェントが依頼を理解し、最適な専門エージェントに仕事を振り分けます。高速実行、計画してから実行、スキルの中から進め方を選ぶこともできます。',
    roster: [
      ['Webリサーチ', '検索・地図・論文まで調べる'], ['Word文書', '作成・編集・翻訳・分析'], ['Excel', '数式・グラフ・ピボット付きのブック'], ['PowerPoint', 'ブランドに合わせた資料'],
      ['HWP文書', 'HWPXの作成・編集、HWPの翻訳'], ['PDF', 'テキスト・レイアウト抽出、Word変換'], ['データ可視化', 'グラフ・図・Mermaid'], ['コード実行', 'サンドボックスでコードを実行'],
      ['画像', '画像の生成・編集・認識'], ['マルチモーダル', '画像・音声・動画を理解'], ['プロジェクトWiki', '社内文書にもとづく回答'], ['社内ツール', 'Slack・Jira・Gmailなどで実行'],
    ],
  },
  docs: {
    eyebrow: 'ドキュメントエージェント', title: ['Word・Excel・PowerPointを', '会話で編集。'],
    sub: 'ファイルをアップロードして変更内容を伝えると、ドキュメントエージェントが元の書式を保ったまま編集します。エディタですぐに確認し、さらに手直しもできます。',
    points: [['元の書式のまま', '表・リスト・スタイルを保ったまま、必要な部分だけを直します。'], ['プロジェクトで管理', '関連する文書をプロジェクトにまとめ、横断的に参照して作業します。'], ['出典が残るWiki', 'プロジェクトの文書からWikiを作り、原文の位置とともに要点を確認できます。']],
    wiki: {
      original: '原本', wiki: 'Wiki', project: 'ノースウィンド営業本部', refresh: '更新', files: ['2026下期_営業戦略.docx', '顧客サポート運用ポリシー.docx', 'Q3_地域別売上.xlsx'],
      keyPoints: '重要ポイント', concepts: ['価格ポリシー', '競合企業', '成果指標', '応答時間の基準', '営業戦略'], edit: '編集',
      docTitle: '顧客サポート運用ポリシー v3.2',
      docSections: [['1. 目的', '顧客からの問い合わせに一貫した品質で対応するための基準と手順を定める。'], ['2. 応答時間の基準（SLA）', '緊急（サービス停止）：30分以内に一次対応、4時間以内に解決'], ['3. エスカレーション手順', '一次対応で解決しない問い合わせは技術サポートチームへ引き継ぐ。'], ['4. 返金と補償', 'サービス停止が月累計4時間を超えた場合、月額料金の10%をクレジットで補償する。']],
      summary: '顧客からの問い合わせに一貫した品質で対応するための基準と手順をまとめた指針。優先度別の応答時間、エスカレーション手順、補償基準を扱う。',
      coreTitle: '主な内容',
      core: [['応答時間の基準（SLA）', '緊急の問い合わせは30分以内に一次対応し、4時間以内の解決を原則とする。'], ['エスカレーション手順', '一次対応で解決しない問い合わせは技術サポートチームへ引き継ぐ。'], ['返金と補償', '月累計の停止が4時間を超えると、月額料金の10%をクレジットで補償する。']],
    },
  },
  integrations: {
    eyebrow: '社内ツール連携', title: ['いつもの業務ツールと、', 'そのままつながります。'],
    sub: 'Slack、Notion、Jira・Confluence、Gmail、Googleカレンダー、Microsoft Teams、Outlook、GitHubまで。必要なサービスを接続すると、エージェントが会話の中で探し、整理し、実行します。',
    cards: [['複数のツールをまとめて', 'チャットで見つけた内容を、課題管理や文書へそのまま移します。'], ['実行前に確認', 'メール送信や課題作成など変更を伴う作業は、承認後に実行します。'], ['必要なツールだけオン', '会話ごとに使うサービスを選んで切り替えられます。']],
    flow: { from: '#営業', message: 'B社から今月の月次レポートが届いていないそうです。', to: 'SALES-232', result: 'B社 月次レポート未着の問い合わせ' },
    approval: { label: 'MCP実行権限', title: 'Gmailでメールを送信します。', preview: '実行する内容', fields: [['宛先', 'partner@northwind.example'], ['件名', '10月の共同ウェビナー日程のご案内']], run: '実行', deny: '実行しない' },
    toggles: { title: 'プラグイン接続', items: [['Slack', true], ['Atlassian', true], ['Notion', true], ['Gmail', false], ['Google Calendar', false]] },
  },
  teams: {
    eyebrow: 'チームで使う', title: ['チーム全員で使う', 'AIワークスペース。'],
    sub: 'チームワークスペースを作って同僚を招待しましょう。うまくいく進め方はスキルとして共有し、利用状況はひと目で管理できます。',
    cards: [['チームワークスペース', 'メンバー、トークン、保存容量をチーム単位で管理します。'], ['社内スキルの共有', '繰り返しの業務をスキルにして、会社全体で共有します。'], ['DM・グループチャット', '同僚と会話しながら、成果物をその場で共有します。'], ['利用状況ダッシュボード', '実行回数やトークン、進行中の作業を確認します。']],
    team: { name: 'ノースウィンド営業本部', role: '管理者', members: 'メンバー', tokens: '残りトークン', storage: 'ストレージ', usage: 'トークン残量', created: '2026年9月1日作成' },
    skills: { tabs: ['マイスキル', 'リファレンススキル', '共有スキル'], items: [['週次営業レポート', '営業企画チーム · 共有38回'], ['提案書レビューのチェックリスト', '経営企画チーム · 共有21回'], ['顧客ミーティングの要約', 'カスタマーサクセス · 共有17回']] },
    chat: { group: '営業本部 · 下期戦略', messages: [['高橋 健', '3章の目標表、最新版を共有してもらえますか？'], ['askyour.ai', '「2026年下期 営業戦略」の最新版を共有しました。'], ['佐藤 美咲', '確認しました。今日の会議資料に使います。']] },
    dash: { title: '利用状況ダッシュボード', stats: [['直近30日の利用', '1,284回'], ['総トークン', '18.6M'], ['平均所要時間', '21.9秒']], activity: 'アクティビティ' },
    controls: [['招待リンクで参加', 'リンクひとつでメンバーを招待できます。'], ['トークン・容量の上限', 'チーム単位で利用量を配分して管理します。'], ['実行前の承認', '変更を伴う作業は確認後にのみ実行されます。'], ['パスワード付き共有', '会話の共有リンクにパスワードを設定できます。']],
  },
  everyday: {
    eyebrow: '毎日の業務ツール', title: ['エージェントだけでなく、', '毎日使うツールもそろっています。'], sub: '記憶して、声で聞いて、翻訳して、業界の動きを追う。すべてひとつのワークスペースで。',
    cards: [['エージェントメモリ', '仕事の文脈や好みを記憶し、内容は自分で確認・修正できます。'], ['ライブエージェント', 'リアルタイムの音声で質問し、検索やツールも一緒に使えます。'], ['翻訳', 'ビジネス・学術ドメインと用語集で、文章や文書を翻訳します。'], ['フィード', '関心分野のニュースやブログ、動画を毎日まとめて届けます。']],
    memory: { title: '私が理解している内容', items: [['業務の文脈', '下期営業戦略の文書を毎週更新している'], ['仕事の進め方', 'レポートは要約と表を先に置くのが好み']], evidence: '根拠を4件見る' },
    live: { status: '聞いています', user: '午後のミーティング前に、B社の最近の課題だけ手短に教えて。', agent: '今週のB社の課題は、月次レポート未着の問い合わせが1件です。' },
    translate: { styles: ['一般', '学術', 'ビジネス'], source: '来期の共同提案のスケジュールを確定してください。', target: 'Please confirm the schedule for next quarter’s joint proposal.', glossary: '用語集' },
    feed: { label: 'DAILY FEED', items: [['ニュース', '製造業のAI導入、文書業務から急速に拡大'], ['ブログ', 'B2B営業チームがエージェントを活用する5つの方法']] },
    more: [['ファイルストレージ', 'アップロード・作成したファイルを一か所に'], ['ウィジェット', 'カレンダー・メール・メモを一画面に'], ['ツールボックス', '電卓・日付・JSONなどの便利ツール'], ['会話の共有', '役立つ会話をリンクで共有']],
  },
  cases: {
    eyebrow: '導入事例', title: ['企業ごとに異なる課題に、', '最適な形で応えています。'],
    sub: 'セキュリティ要件の厳しいオンプレミス環境から、社内ナレッジの体系化、ERP連携による業務自動化まで。各企業のポリシーと業務プロセスに合わせてaskyour.aiを構築した事例です。',
    items: [
      { company: 'S社', kind: 'オンプレミス構築', title: 'データが社外に出ないAIワークスペース', body: '個人情報や社内文書など、セキュリティが重要なデータを扱う環境に合わせてaskyour.aiをオンプレミスで構築し、社内ポリシーに沿ってカスタマイズしました。すべてのデータは社内インフラ内で処理・保管され、外部のLLM APIへ送信されることはありません。', points: ['オンプレミス構築・カスタマイズ', '社内インフラ内でのデータ処理', '外部LLM APIへの送信を遮断'] },
      { company: 'P社', kind: 'ナレッジオントロジー', title: '散在する暗黙知を、エージェントが活用できる知識へ', body: '部門や文書のあちこちに散在していた社内ナレッジと暗黙知をオントロジーとして構造化しました。これを搭載したエージェントフレームワークを提供し、組織に蓄積されたノウハウを現場ですぐに活用できるようにしました。', points: ['社内文書・暗黙知の統合', 'オントロジーによる知識の構造化', '社内ナレッジを搭載したエージェント基盤'] },
      { company: 'K社', kind: 'ERP連携の自動化', title: '手作業の伝票処理をエージェントパイプラインへ', body: 'ERPで手作業により行っていた伝票入力の繰り返し業務を自動化しました。askyour.aiとERPを連携し、エージェントが伝票処理に必要な情報を抽出してERPに入力するまでの全工程を、一つのパイプラインで処理します。', points: ['askyour.aiとERPの連携', '伝票情報の自動抽出', '抽出から入力までを自動化'] },
    ],
    onprem: { title: '配置環境', badge: 'On-premises', rows: [['LLM', '社内モデル'], ['文書・データ', '社内ストレージ']], blocked: ['外部LLM API', '遮断'] },
    ontology: { title: '社内ナレッジオントロジー', docs: ['設備点検マニュアル.pdf', '作業標準書.hwpx', '議事録.docx'], concepts: ['設備', '工程', '品質基準'], agent: 'エージェントに搭載済み' },
    erp: { title: '伝票処理パイプライン', extract: '伝票情報を抽出', fields: [['取引先', 'ノースウィンド株式会社'], ['金額', '¥135,000'], ['勘定科目', '消耗品費']], done: 'ERPへ入力完了', voucher: '伝票 2026-0142' },
  },
  faq: {
    eyebrow: 'FAQ', title: 'よくある質問',
    items: [
      { q: 'チームで導入できますか？', a: 'チームワークスペースを作成し、招待リンクで同僚を招待できます。チーム管理画面でメンバー、トークン、保存容量を確認・管理できます。組織での導入は「導入のご相談」からご連絡ください。' },
      { q: 'どのファイルを扱えますか？', a: 'ドキュメントエージェントでWord（DOCX）、Excel（XLSX）、PowerPoint（PPTX）を編集し、PDFを閲覧できます。統合エージェントは韓国のHWPX文書の作成・編集、PDFのテキスト抽出、画像分析にも対応しています。' },
      { q: 'どの業務ツールと連携できますか？', a: 'Slack、Notion、Jira・Confluence（Atlassian）、Gmail、Googleカレンダー、Microsoft Teams、Outlookのメール・カレンダー、GitHub、Discordをプラグインとして接続できます。Google DriveとOneDriveのファイルは会話に直接添付できます。' },
      { q: 'エージェントが勝手に作業を実行しませんか？', a: 'メール送信や課題作成など変更を伴う作業は、既定で実行前に承認を求めます。実行内容を確認したうえで、実行するか断るかを選べます。' },
      { q: '料金はどうなっていますか？', a: 'Googleアカウントで登録すると、無料プランですぐに始められます。さらに利用量が必要な場合は、料金ページでプランを比較してください。' },
    ],
  },
  closing: { title: ['あなたの次の仕事を、', 'Askyour.aiと一緒に。'], sub: '無料ではじめて、エンタープライズ導入はいつでもご相談ください。' },
  footer: { tagline: '仕事をやり遂げるAIエージェント・ワークスペース', product: '製品', company: 'お問い合わせ', legal: '規約', terms: '利用規約', privacy: 'プライバシーポリシー', email: 'メールで問い合わせ' },
};

const zhCN: LandingCopy = {
  ...en,
  nav: { capabilities: '功能', cases: '客户案例', pricing: '价格', menu: '菜单' },
  login: '通过 Google 开始', signIn: '开始使用', loading: '正在前往登录', loginError: '无法开始登录，请重试。', contact: '咨询企业方案', language: '选择语言', skip: '跳到正文',
  hero: {
    title: 'Meet your own AI agent',
    lead: '一个界面，一条流程。',
    sub: 'askyour.ai 是自动化重复工作的一站式 AI 工作空间。从适配企业内部环境的本地化部署，到企业定制 AI 开发，均可灵活支持。',
  },
  demo: { label: '产品演示', play: '播放演示', pause: '暂停演示', replay: '从头播放', url: 'askyour.ai' },
  ui: {
    personal: '个人', team: '团队', workspace: '工作区', teamWorkspace: '团队工作区', personalSection: '个人', manage: '管理',
    agent: '智能助手', live: '实时代理', docAgent: '文档代理', teamManage: '团队管理', widget: '小组件', feed: '资讯', tools: '工具箱',
    dm: '私信', storage: '文件存储', plugin: '插件', memory: '助手记忆', profile: '王思远', profileSub: '北风销售本部',
    placeholder: '输入消息…', disclaimer: 'Agent可能会出错，请自行确认重要信息。', welcome: '实时与代理对话', steps: '{n} 个步骤', done: '完成', saved: '已保存', export: '导出',
    menus: ['文件', '格式', '插入'], fit: '适合页面', paragraph: '正文',
  },
  scenes: [
    {
      ...en.scenes[0], tab: '调研与报告',
      prompt: '整理 2026 年 B2B SaaS 市场的三大趋势并注明出处，最后用表格总结。',
      steps: [
        { title: '正在理解请求', actor: 'RootAgent' },
        { title: '确定执行方式', actor: 'RootClassifier → FastActionSelector' },
        { title: '调研 B2B SaaS 市场趋势', actor: 'FastActionSelector → WebAgent', body: '检索行业报告、文章和公开资料，提炼出三大核心趋势。', tools: ['WebAgent 已完成网页搜索工具的使用', 'WebAgent 已完成页面读取工具的使用'] },
        { title: '撰写报告', actor: 'WebAgent → BasicAgent' },
      ],
      lead: '2026 年 B2B SaaS 市场的增长由 AI 智能体的引入和计费模式的变化推动。以下是三大主要趋势。',
      heading: '核心趋势概览',
      table: { head: ['趋势', '核心变化', '选型要点'], rows: [['融合 AI 智能体', '辅助功能 → 自动执行工作', '对接内部系统'], ['垂直 SaaS 扩张', '面向行业和岗位的工作流', '上线速度与 ROI'], ['计费模式转变', '按席位 → 按用量和成果', '预算可预测']] },
      sources: '8 个来源',
    },
    {
      ...en.scenes[1], tab: '文档编辑',
      prompt: '在第 3 章目标表中添加“实施方案”一列，并在第 5 章补充一段汇率波动风险及应对措施。',
      fileName: '北风_2026下半年_销售战略.docx',
      steps: [
        { title: '正在理解请求', actor: 'RootAgent' },
        { title: '应用文档修改', actor: 'FastActionSelector → CanvasEditAgent', tools: ['CanvasEditAgent 已完成文档结构检查', 'CanvasEditAgent 已完成表格编辑'] },
      ],
      doc: {
        title: '2026 下半年销售战略（草案）',
        overview: ['1. 概述', '上半年销售额完成目标的 92%，老客户续约率达到 94%，表现良好。'],
        market: ['2. 市场现状', ['以中型企业为主，业务自动化需求持续增长', '竞争对手 A 通过降价抢占中小企业市场', '客户最看重上线速度和内部系统集成']],
        goals: ['3. 下半年核心目标', '同时推进新客户获取和客单价提升。'],
        table: { head: ['指标', '上半年实绩', '下半年目标'], rows: [['新客户数', '38 家', '60 家'], ['平均合同金额', '23 万元', '27 万元'], ['续约率', '94%', '95% 以上']] },
        addedHead: '实施方案', addedCells: ['制造业研讨会、合作伙伴联合方案', '高级套餐升级活动', '每季度客户成功回顾'],
        plan: ['4. 执行计划', ['7 月：举办 2 场制造业专场研讨会', '8 月：推出合作伙伴联合方案包', '9 月：开展高级套餐升级活动']],
        risk: ['5. 风险与应对', '为应对经济放缓导致的预算冻结，准备免费试点和分阶段导入方案。'],
        addedRisk: '汇率波动可能推高海外解决方案成本，因此主要合同以人民币固定单价签订，并每季度检查汇率敏感度。',
      },
      reply: { lead: '已完成您要求的文档修改。', heading: '已应用的修改', items: ['在第 3 章目标表中添加了“实施方案”一列。', '在第 5 章补充了汇率波动风险及应对措施。'], closing: '请在文档中查看修改内容。' },
    },
    {
      ...en.scenes[2], tab: '数据分析',
      prompt: '根据 Q3 各地区销售文件，用图表展示目标达成率，并总结未达标地区的原因。',
      attachment: '北风_Q3_各地区销售.xlsx',
      steps: [
        { title: '正在理解请求', actor: 'RootAgent' },
        { title: '分析销售数据', actor: 'FastActionSelector → ExcelAgent', tools: ['ExcelAgent 已完成工作表读取'] },
        { title: '生成达成率图表', actor: 'ExcelAgent → DataScienceAgent', tools: ['DataScienceAgent 已完成图表生成'] },
      ],
      chartTitle: 'Q3 各地区目标达成率', target: '目标 100%', series: [['华东', 112], ['华北', 104], ['华南', 91], ['西南', 86]],
      lead: '华东和华北超额完成目标，华南和西南未达到目标。',
      insights: ['两个地区的文档自动化产品销售在 8 月后停滞', '合作伙伴渠道占比低，新客户流入少', '第四季度优先推进合作伙伴联合方案和区域研讨会'],
      output: 'Q3_达成率分析.xlsx',
    },
    {
      ...en.scenes[3], tab: '内部工具集成',
      prompt: '在 Slack 的 #销售团队 频道中找出本周的客户问题，并登记为 Jira 问题。',
      steps: [
        { title: '正在理解请求', actor: 'RootAgent' },
        { title: '查看 Slack 对话', actor: 'FastActionSelector → PluginAgent', tools: ['PluginAgent 已完成 Slack 消息搜索'] },
        { title: '登记 Jira 问题', actor: 'PluginAgent · Atlassian' },
      ],
      approval: { label: 'MCP 执行权限', title: '将在 Jira 中创建 3 个问题。', preview: '将要执行的内容', fields: [['项目', 'SALES'], ['类型', '任务'], ['标题', 'A 公司电子签名错误等 3 项']], run: '执行', deny: '不执行' },
      lead: '已在 Jira 中登记 3 个问题，并附上了 Slack 原始消息链接。',
      issues: [['SALES-231', 'A 公司合同电子签名错误'], ['SALES-232', 'B 公司月度报告缺失咨询'], ['SALES-233', 'C 公司申请增加用户权限']],
    },
  ],
  intro: {
    eyebrow: '关于 askyour.ai',
    lead1: 'askyour.ai 在 agent orchestration layer 之上整合并连接多种 AI 能力，根据使用目的提供自然衔接的 AI 体验。',
    body1: ' 个人可以更轻松地使用，企业可以更灵活地构建，从日常工作到企业级环境都能全面应对。',
    lead2: '复杂的智能体构建，交给 askyour.ai。',
    body2: ' 多智能体系统由多个 AI 智能体组成，它们各自自主运行，又为实现共同目标而协作：理解用户的输入、做出决策并执行任务。',
  },
  capabilities: {
    ...en.capabilities,
    eyebrow: '能做什么', title: ['重复性的工作，', '交给智能体。'], sub: '从报告初稿到数据整理、演示文稿，像拜托同事一样提出请求即可。',
    cards: [
      { lead: '调研与报告', body: '搜索并阅读网络和文档资料，整理成附带出处的报告。', prompt: '对比 3 家竞争对手的价格策略' },
      { lead: '文档初稿', body: '根据会议记录和资料撰写提案、报告初稿。', prompt: '根据会议记录写一份提案初稿' },
      { lead: '数据分析', body: '读取并计算 Excel 文件，用图表和洞察呈现结果。', prompt: '把月度销售趋势画成图表' },
      { lead: '演示文稿', body: '按内容组织幻灯片，并检查设计效果。', prompt: '制作季度业绩汇报幻灯片' },
      { lead: 'HWP 与 PDF', body: '撰写和编辑韩文 HWPX 文档，并从 PDF 中提取内容。', prompt: '用 HWPX 写一份协作请求公文' },
      { lead: '图像生成与分析', body: '制作宣传图片，并识别图片中的内容。', prompt: '制作一张新品推广图片' },
    ],
    mocks: {
      research: { heading: '竞争对手价格策略对比', head: ['公司', '基础价格', '近期变化'], rows: [['A 公司', '每月 499 元', '引入按量计费'], ['B 公司', '每月 699 元', '扩大年付折扣'], ['C 公司', '每月 399 元', '30 天免费试用']], sources: '6 个来源' },
      draft: { file: '业务自动化_导入提案.docx', title: '业务自动化导入提案', lines: ['1. 提案背景', '2. 导入范围与时间表'], highlight: '导入效果：周报撰写时间缩短一半。' },
      chart: { title: '月度销售趋势', months: ['7月', '8月', '9月', '10月', '11月', '12月'], file: '月度销售.xlsx' },
      slides: ['第三季度业绩汇报', '销售概览', '各地区业绩', '下季度计划'],
      hwp: { org: '北风韩国', to: '收件  各部门负责人', subject: '标题  关于下半年培训日程的协作请求', body: '下半年岗位培训安排如下，请各部门回复参加人数。' },
      image: 'promotion_banner.png',
    },
    rosterTitle: '12 个专业智能体协同工作。',
    rosterSub: '智能助手理解请求后，把任务分配给合适的专业智能体。也可以选择快速执行、先规划再执行或技能模式。',
    roster: [
      ['网络调研', '搜索、地图、论文'], ['Word 文档', '撰写、编辑、翻译、分析'], ['Excel', '含公式、图表、透视表的工作簿'], ['PowerPoint', '符合品牌风格的演示文稿'],
      ['HWP 文档', 'HWPX 撰写与编辑、HWP 翻译'], ['PDF', '提取文本与版式、转换为 Word'], ['数据可视化', '图表、示意图、Mermaid'], ['代码执行', '在沙箱中运行代码'],
      ['图像', '图像生成、编辑、识别'], ['多模态分析', '理解图像、音频、视频'], ['项目知识库', '基于内部文档回答'], ['内部工具', '在 Slack、Jira、Gmail 等中执行'],
    ],
  },
  docs: {
    eyebrow: '文档代理', title: ['Word、Excel、PowerPoint，', '用对话来编辑。'],
    sub: '上传文件并说明要修改的内容，文档代理会在保留原有格式的前提下完成编辑。可在编辑器中直接查看并继续修改。',
    points: [['保留原有格式', '表格、列表和样式保持不变，只修改需要改的部分。'], ['按项目管理', '把相关文档集中到项目中，一起参考着工作。'], ['可溯源的知识库', '用项目文档生成知识库，查看要点时附带原文位置。']],
    wiki: {
      original: '原文', wiki: '知识库', project: '北风销售本部', refresh: '更新', files: ['2026下半年_销售战略.docx', '客户支持运营政策.docx', 'Q3_各地区销售.xlsx'],
      keyPoints: '核心要点', concepts: ['价格政策', '竞争企业', '绩效指标', '响应时间标准', '销售战略'], edit: '编辑',
      docTitle: '客户支持运营政策 v3.2',
      docSections: [['1. 目的', '规定以一致的质量响应客户咨询的标准和流程。'], ['2. 响应时间标准（SLA）', '紧急（服务中断）：30 分钟内首次响应，4 小时内解决'], ['3. 升级流程', '首次处理未能解决的咨询转交技术支持团队。'], ['4. 退款与补偿', '服务中断当月累计超过 4 小时，按当月费用的 10% 以额度补偿。']],
      summary: '规定以一致的质量处理客户咨询的标准和流程，涵盖按优先级划分的响应时间、升级流程和补偿标准。',
      coreTitle: '核心内容',
      core: [['响应时间标准（SLA）', '紧急咨询原则上 30 分钟内首次响应，4 小时内解决。'], ['升级流程', '首次处理未解决的咨询转交技术支持团队。'], ['退款与补偿', '当月累计中断超过 4 小时，按当月费用的 10% 以额度补偿。']],
    },
  },
  integrations: {
    eyebrow: '内部工具集成', title: ['与现有的办公工具', '直接连接。'],
    sub: 'Slack、Notion、Jira 与 Confluence、Gmail、Google 日历、Microsoft Teams、Outlook、GitHub 等。连接所需服务后，智能体会在对话中查找、整理并执行。',
    cards: [['一次调用多个工具', '把聊天中的信息直接转到问题跟踪和文档中。'], ['执行前先确认', '发送邮件、创建问题等会产生变更的操作，需批准后才会执行。'], ['只开启需要的工具', '每个对话都可以选择启用或关闭要用的服务。']],
    flow: { from: '#销售团队', message: 'B 公司说这个月的月度报告还没收到。', to: 'SALES-232', result: 'B 公司月度报告缺失咨询' },
    approval: { label: 'MCP 执行权限', title: '将通过 Gmail 发送邮件。', preview: '将要执行的内容', fields: [['收件人', 'partner@northwind.example'], ['主题', '10 月联合研讨会日程通知']], run: '执行', deny: '不执行' },
    toggles: { title: '插件连接', items: [['Slack', true], ['Atlassian', true], ['Notion', true], ['Gmail', false], ['Google Calendar', false]] },
  },
  teams: {
    eyebrow: '团队协作', title: ['整个团队共用的', 'AI 工作空间。'],
    sub: '创建团队工作空间并邀请同事。好用的工作方法可以作为技能共享，用量也能一目了然地管理。',
    cards: [['团队工作空间', '按团队管理成员、Token 和存储空间。'], ['共享企业技能', '把重复性工作做成技能，在全公司共享。'], ['私信与群聊', '和同事交流的同时，随时分享成果。'], ['用量仪表盘', '查看使用次数、Token 和进行中的任务。']],
    team: { name: '北风销售本部', role: '管理员', members: '成员', tokens: '剩余 Token', storage: '存储', usage: 'Token 余量', created: '2026 年 9 月 1 日创建' },
    skills: { tabs: ['我的技能', '参考技能', '共享技能'], items: [['每周销售报告', '销售运营组 · 共享 38 次'], ['提案审核清单', '战略规划组 · 共享 21 次'], ['客户会议摘要', '客户成功组 · 共享 17 次']] },
    chat: { group: '销售本部 · 下半年战略', messages: [['李浩然', '能分享一下第 3 章目标表的最新版本吗？'], ['askyour.ai', '已分享《2026 下半年销售战略》最新版。'], ['王思远', '收到，今天开会就用这个。']] },
    dash: { title: '用量仪表盘', stats: [['近 30 天使用', '1,284 次'], ['Token 总量', '18.6M'], ['平均耗时', '21.9 秒']], activity: '活动' },
    controls: [['通过邀请链接加入', '一条链接即可邀请团队成员。'], ['Token 与存储上限', '按团队分配并管理用量。'], ['执行前审批', '变更操作仅在确认后执行。'], ['密码保护的分享', '分享对话时可以设置密码。']],
  },
  everyday: {
    eyebrow: '日常办公工具', title: ['除了智能体，', '还有每天都用的工具。'], sub: '记忆、语音提问、翻译、跟进行业动态，都在同一个工作空间完成。',
    cards: [['助手记忆', '记住工作背景和偏好，内容可自行查看和修改。'], ['实时代理', '用实时语音提问，同时调用搜索和工具。'], ['翻译', '借助商务、学术领域和术语表翻译句子和文档。'], ['资讯', '每天汇总关注领域的新闻、博客和视频。']],
    memory: { title: '我所了解的内容', items: [['工作背景', '每周更新下半年销售战略文档'], ['工作方式', '偏好报告先给摘要和表格']], evidence: '查看 4 条依据' },
    live: { status: '正在聆听', user: '下午开会前，简单告诉我 B 公司最近的问题。', agent: '本周 B 公司有 1 个问题：月度报告缺失咨询。' },
    translate: { styles: ['通用', '学术', '商务'], source: '请确认下季度联合提案的日程。', target: 'Please confirm the schedule for next quarter’s joint proposal.', glossary: '术语表' },
    feed: { label: 'DAILY FEED', items: [['新闻', '制造业 AI 应用从文档工作开始快速普及'], ['博客', 'B2B 销售团队使用智能体的 5 种方式']] },
    more: [['文件存储', '上传和生成的所有文件集中管理'], ['小组件', '日历、邮件、备忘录一屏查看'], ['工具箱', '计算器、日期、JSON 等小工具'], ['对话分享', '通过链接分享有用的对话']],
  },
  cases: {
    eyebrow: '客户案例', title: ['每家企业的挑战不同，', '我们提供量身定制的方案。'],
    sub: '从安全要求严格的本地化部署环境，到企业知识体系化，再到 ERP 集成自动化。以下是根据各企业的安全策略与业务方式构建 askyour.ai 的案例。',
    items: [
      { company: 'S 公司', kind: '本地化部署', title: '数据不出企业的 AI 工作空间', body: '针对处理个人信息与内部文档等高敏感数据的环境，以本地化方式部署 askyour.ai，并按照企业政策进行定制。所有数据均在企业内部基础设施中处理与存储，不会向外部 LLM API 发送任何信息。', points: ['本地化部署与定制', '数据在内部基础设施中处理', '阻断向外部 LLM API 传输'] },
      { company: 'P 公司', kind: '知识本体', title: '将分散的隐性知识，变为智能体可用的知识', body: '将散落在各部门与文档中的内部知识和隐性知识构建为本体结构，并提供搭载这些知识的智能体框架，让组织积累的经验能在业务一线即时发挥作用。', points: ['整合内部文档与隐性知识', '基于本体的知识结构化', '搭载企业知识的智能体框架'] },
      { company: 'K 公司', kind: 'ERP 集成自动化', title: '把手工凭证处理交给智能体流水线', body: '将在 ERP 中手工录入凭证的重复工作实现自动化。通过集成 askyour.ai 与 ERP，由智能体提取凭证处理所需的信息，并把从提取到录入 ERP 的全过程整合为一条流水线。', points: ['askyour.ai 与 ERP 集成', '自动提取凭证信息', '从提取到录入的流水线自动化'] },
    ],
    onprem: { title: '部署环境', badge: 'On-premises', rows: [['LLM', '内部模型'], ['文档·数据', '内部存储']], blocked: ['外部 LLM API', '已阻断'] },
    ontology: { title: '企业知识本体', docs: ['设备点检手册.pdf', '作业标准.hwpx', '会议纪要.docx'], concepts: ['设备', '工序', '质量标准'], agent: '已搭载至智能体' },
    erp: { title: '凭证处理流水线', extract: '提取凭证信息', fields: [['往来单位', '北风有限公司'], ['金额', '¥8,600.00'], ['会计科目', '办公耗材']], done: '已录入 ERP', voucher: '凭证 2026-0142' },
  },
  faq: {
    eyebrow: '常见问题', title: '导入前的常见问题',
    items: [
      { q: '可以以团队为单位使用吗？', a: '可以创建团队工作空间，并通过邀请链接邀请同事。在团队管理页面可以查看和管理成员、Token 与存储空间。企业导入请通过“咨询企业方案”联系我们。' },
      { q: '支持哪些文件？', a: '文档代理可以编辑 Word（DOCX）、Excel（XLSX）、PowerPoint（PPTX），并可查看 PDF。智能助手还支持韩文 HWPX 文档的撰写与编辑、PDF 文本提取和图像分析。' },
      { q: '可以连接哪些办公工具？', a: '可以通过插件连接 Slack、Notion、Jira 与 Confluence（Atlassian）、Gmail、Google 日历、Microsoft Teams、Outlook 邮件与日历、GitHub、Discord。Google Drive 和 OneDrive 的文件可以直接附加到对话中。' },
      { q: '智能体会擅自执行操作吗？', a: '发送邮件、创建问题等会产生变更的操作，默认会在执行前请求批准。确认执行内容后，您可以选择执行或拒绝。' },
      { q: '如何收费？', a: '使用 Google 账号注册即可立即使用免费方案。如需更多用量，可在价格页面比较各方案。' },
    ],
  },
  closing: { title: ['你的下一项工作，', '与 Askyour.ai 一起完成。'], sub: '免费开始使用，企业级部署随时联系我们。' },
  footer: { tagline: '把工作做完的 AI 智能体工作空间', product: '产品', company: '联系我们', legal: '条款', terms: '服务条款', privacy: '隐私政策', email: '邮件咨询' },
};

const zhTW: LandingCopy = {
  ...zhCN,
  nav: { capabilities: '功能', cases: '導入案例', pricing: '價格', menu: '選單' },
  login: '透過 Google 開始', signIn: '開始使用', loading: '正在前往登入', loginError: '無法開始登入，請再試一次。', contact: '洽詢企業方案', language: '選擇語言', skip: '跳到主要內容',
  hero: {
    title: 'Meet your own AI agent',
    lead: '一個畫面，一條流程。',
    sub: 'askyour.ai 是自動化重複工作的一站式 AI 工作空間。從貼合企業內部環境的地端部署，到企業客製化 AI 開發，皆可彈性支援。',
  },
  demo: { label: '產品示範', play: '播放示範', pause: '暫停示範', replay: '從頭播放', url: 'askyour.ai' },
  ui: {
    personal: '個人', team: '團隊', workspace: '工作區', teamWorkspace: '團隊工作區', personalSection: '個人', manage: '管理',
    agent: '智慧助手', live: '即時代理', docAgent: '文件代理', teamManage: '團隊管理', widget: '小工具', feed: '資訊', tools: '工具箱',
    dm: '私訊', storage: '檔案儲存', plugin: '外掛程式', memory: '助手記憶', profile: '陳怡君', profileSub: '北風業務本部',
    placeholder: '輸入訊息…', disclaimer: 'Agent可能會出錯，請自行確認重要資訊。', welcome: '即時與代理對話', steps: '{n} 個步驟', done: '完成', saved: '已儲存', export: '匯出',
    menus: ['檔案', '格式', '插入'], fit: '符合頁面', paragraph: '一般文字',
  },
  scenes: [
    {
      ...zhCN.scenes[0], tab: '研究與報告',
      prompt: '整理 2026 年 B2B SaaS 市場的三大趨勢並附上出處，最後用表格摘要。',
      steps: [
        { title: '正在理解請求', actor: 'RootAgent' },
        { title: '決定執行方式', actor: 'RootClassifier → FastActionSelector' },
        { title: '研究 B2B SaaS 市場趨勢', actor: 'FastActionSelector → WebAgent', body: '搜尋產業報告、文章與公開資料，歸納出三大核心趨勢。', tools: ['WebAgent 已完成網頁搜尋工具的使用', 'WebAgent 已完成頁面讀取工具的使用'] },
        { title: '撰寫報告', actor: 'WebAgent → BasicAgent' },
      ],
      lead: '2026 年 B2B SaaS 市場的成長，由 AI 代理的導入與計費模式的轉變所帶動。以下整理三大主要趨勢。',
      heading: '核心趨勢摘要',
      table: { head: ['趨勢', '核心變化', '導入重點'], rows: [['結合 AI 代理', '輔助功能 → 自動執行工作', '串接內部系統'], ['垂直 SaaS 擴張', '針對產業與職務的工作流程', '導入速度與 ROI'], ['計費模式轉變', '按席位 → 按用量與成果', '預算可預測']] },
      sources: '8 個來源',
    },
    {
      ...zhCN.scenes[1], tab: '文件編輯',
      prompt: '在第 3 章目標表中新增「執行方案」一欄，並在第 5 章補充一段匯率波動風險與因應措施。',
      fileName: '北風_2026下半年_業務策略.docx',
      steps: [
        { title: '正在理解請求', actor: 'RootAgent' },
        { title: '套用文件修改', actor: 'FastActionSelector → CanvasEditAgent', tools: ['CanvasEditAgent 已完成文件結構檢查', 'CanvasEditAgent 已完成表格編輯'] },
      ],
      doc: {
        title: '2026 下半年業務策略（草案）',
        overview: ['1. 概述', '上半年營收達成目標的 92%，既有客戶續約率為 94%，表現穩健。'],
        market: ['2. 市場現況', ['以中型企業為主，業務自動化需求持續成長', '競爭對手 A 以降價搶攻中小企業市場', '客戶最重視導入速度與內部系統整合']],
        goals: ['3. 下半年核心目標', '同時推動新客戶開發與客單價提升。'],
        table: { head: ['指標', '上半年實績', '下半年目標'], rows: [['新客戶數', '38 家', '60 家'], ['平均合約金額', 'NT$95 萬', 'NT$115 萬'], ['續約率', '94%', '95% 以上']] },
        addedHead: '執行方案', addedCells: ['製造業研討會、夥伴聯合提案', '高階方案升級活動', '每季客戶成功檢討'],
        plan: ['4. 執行計畫', ['7 月：舉辦 2 場製造業專場研討會', '8 月：推出夥伴聯合提案方案', '9 月：推動高階方案升級活動']],
        risk: ['5. 風險與因應', '為因應景氣趨緩造成的預算凍結，準備免費試用與分階段導入方案。'],
        addedRisk: '匯率波動可能推高海外解決方案成本，因此主要合約以新台幣固定單價簽訂，並每季檢視匯率敏感度。',
      },
      reply: { lead: '已完成您要求的文件修改。', heading: '已套用的修改', items: ['在第 3 章目標表中新增了「執行方案」一欄。', '在第 5 章補充了匯率波動風險與因應措施。'], closing: '請在文件中確認修改內容。' },
    },
    {
      ...zhCN.scenes[2], tab: '資料分析',
      prompt: '根據 Q3 各區域營收檔案，用圖表呈現目標達成率，並整理未達標區域的原因。',
      attachment: '北風_Q3_各區域營收.xlsx',
      steps: [
        { title: '正在理解請求', actor: 'RootAgent' },
        { title: '分析營收資料', actor: 'FastActionSelector → ExcelAgent', tools: ['ExcelAgent 已完成工作表讀取'] },
        { title: '產生達成率圖表', actor: 'ExcelAgent → DataScienceAgent', tools: ['DataScienceAgent 已完成圖表產生'] },
      ],
      chartTitle: 'Q3 各區域目標達成率', target: '目標 100%', series: [['台北', 112], ['新竹', 104], ['台中', 91], ['高雄', 86]],
      lead: '台北與新竹超越目標，台中與高雄則未達目標。',
      insights: ['兩個區域的文件自動化產品營收在 8 月後停滯', '夥伴通路占比低，新客戶流入較少', '第四季優先推動夥伴聯合提案與區域研討會'],
      output: 'Q3_達成率分析.xlsx',
    },
    {
      ...zhCN.scenes[3], tab: '內部工具整合',
      prompt: '在 Slack 的 #業務團隊 頻道找出本週的客戶問題，並登錄為 Jira 議題。',
      steps: [
        { title: '正在理解請求', actor: 'RootAgent' },
        { title: '查看 Slack 對話', actor: 'FastActionSelector → PluginAgent', tools: ['PluginAgent 已完成 Slack 訊息搜尋'] },
        { title: '登錄 Jira 議題', actor: 'PluginAgent · Atlassian' },
      ],
      approval: { label: 'MCP 執行權限', title: '將在 Jira 建立 3 個議題。', preview: '將要執行的內容', fields: [['專案', 'SALES'], ['類型', '工作'], ['標題', 'A 公司電子簽章錯誤等 3 項']], run: '執行', deny: '不執行' },
      lead: '已在 Jira 登錄 3 個議題，並附上 Slack 原始訊息連結。',
      issues: [['SALES-231', 'A 公司合約電子簽章錯誤'], ['SALES-232', 'B 公司月報缺漏詢問'], ['SALES-233', 'C 公司申請增加使用者權限']],
    },
  ],
  intro: {
    eyebrow: '關於 askyour.ai',
    lead1: 'askyour.ai 在 agent orchestration layer 之上整合並串接多種 AI 能力，依使用目的提供自然銜接的 AI 體驗。',
    body1: ' 個人能更輕鬆地運用，企業能更彈性地建置，從日常工作到企業級環境都能全面因應。',
    lead2: '複雜的代理建置，交給 askyour.ai。',
    body2: ' 多代理系統由多個 AI 代理組成，它們各自自主運作，又為達成共同目標而協作：理解使用者的輸入、做出決策並執行任務。',
  },
  capabilities: {
    ...zhCN.capabilities,
    eyebrow: '能做什麼', title: ['重複性的工作，', '交給代理。'], sub: '從報告初稿到資料整理、簡報，就像請同事幫忙一樣提出請求即可。',
    cards: [
      { lead: '研究與報告', body: '搜尋並閱讀網路與文件資料，整理成附出處的報告。', prompt: '比較 3 家競爭對手的價格策略' },
      { lead: '文件初稿', body: '根據會議記錄與資料撰寫提案、報告初稿。', prompt: '根據會議記錄寫一份提案初稿' },
      { lead: '資料分析', body: '讀取並計算 Excel 檔案，用圖表與洞察呈現結果。', prompt: '把每月營收趨勢畫成圖表' },
      { lead: '簡報', body: '依內容規劃投影片，並檢查設計。', prompt: '製作季度業績報告簡報' },
      { lead: 'HWP 與 PDF', body: '撰寫與編輯韓文 HWPX 文件，並從 PDF 擷取內容。', prompt: '用 HWPX 寫一份協助請求公文' },
      { lead: '圖片生成與分析', body: '製作宣傳圖片，並辨識圖片中的內容。', prompt: '製作一張新品推廣圖片' },
    ],
    mocks: {
      research: { heading: '競爭對手價格策略比較', head: ['公司', '基本價格', '近期變化'], rows: [['A 公司', '每月 NT$2,100', '導入按量計費'], ['B 公司', '每月 NT$2,900', '擴大年繳折扣'], ['C 公司', '每月 NT$1,600', '30 天免費試用']], sources: '6 個來源' },
      draft: { file: '業務自動化_導入提案.docx', title: '業務自動化導入提案', lines: ['1. 提案背景', '2. 導入範圍與時程'], highlight: '導入效益：週報撰寫時間縮短一半。' },
      chart: { title: '每月營收趨勢', months: ['7月', '8月', '9月', '10月', '11月', '12月'], file: '每月營收.xlsx' },
      slides: ['第三季業績報告', '營收摘要', '各區域成果', '下季計畫'],
      hwp: { org: '北風韓國', to: '受文者  各部門主管', subject: '主旨  下半年教育訓練日程協助請求', body: '下半年職務訓練安排如下，請各部門回覆參加人數。' },
      image: 'promotion_banner.png',
    },
    rosterTitle: '12 個專業代理協同工作。',
    rosterSub: '智慧助手理解請求後，將工作分配給合適的專業代理。也可以選擇快速執行、先規劃再執行或技能模式。',
    roster: [
      ['網路研究', '搜尋、地圖、論文'], ['Word 文件', '撰寫、編輯、翻譯、分析'], ['Excel', '含公式、圖表、樞紐分析的活頁簿'], ['PowerPoint', '符合品牌風格的簡報'],
      ['HWP 文件', 'HWPX 撰寫與編輯、HWP 翻譯'], ['PDF', '擷取文字與版面、轉換為 Word'], ['資料視覺化', '圖表、示意圖、Mermaid'], ['程式執行', '在沙箱中執行程式'],
      ['圖片', '圖片生成、編輯、辨識'], ['多模態分析', '理解圖片、語音、影片'], ['專案知識庫', '根據內部文件回答'], ['內部工具', '在 Slack、Jira、Gmail 等中執行'],
    ],
  },
  docs: {
    eyebrow: '文件代理', title: ['Word、Excel、PowerPoint，', '用對話來編輯。'],
    sub: '上傳檔案並說明要修改的內容，文件代理會在保留原始格式的前提下完成編輯。可在編輯器中直接確認並繼續修改。',
    points: [['保留原始格式', '表格、清單與樣式維持不變，只修改需要改的部分。'], ['依專案管理', '將相關文件集中到專案中，一起參考著工作。'], ['可追溯的知識庫', '用專案文件建立知識庫，查看重點時附上原文位置。']],
    wiki: {
      original: '原文', wiki: '知識庫', project: '北風業務本部', refresh: '更新', files: ['2026下半年_業務策略.docx', '客戶支援營運政策.docx', 'Q3_各區域營收.xlsx'],
      keyPoints: '核心重點', concepts: ['價格政策', '競爭企業', '績效指標', '回應時間標準', '業務策略'], edit: '編輯',
      docTitle: '客戶支援營運政策 v3.2',
      docSections: [['1. 目的', '規範以一致品質回應客戶詢問的標準與流程。'], ['2. 回應時間標準（SLA）', '緊急（服務中斷）：30 分鐘內首次回應，4 小時內解決'], ['3. 升級流程', '首次處理未能解決的詢問轉交技術支援團隊。'], ['4. 退款與補償', '服務中斷當月累計超過 4 小時，以當月費用的 10% 折抵補償。']],
      summary: '規範以一致品質處理客戶詢問的標準與流程，涵蓋依優先等級區分的回應時間、升級流程與補償標準。',
      coreTitle: '核心內容',
      core: [['回應時間標準（SLA）', '緊急詢問原則上 30 分鐘內首次回應，4 小時內解決。'], ['升級流程', '首次處理未解決的詢問轉交技術支援團隊。'], ['退款與補償', '當月累計中斷超過 4 小時，以當月費用的 10% 折抵補償。']],
    },
  },
  integrations: {
    eyebrow: '內部工具整合', title: ['與現有的工作工具', '直接連結。'],
    sub: 'Slack、Notion、Jira 與 Confluence、Gmail、Google 日曆、Microsoft Teams、Outlook、GitHub 等。連結所需服務後，代理會在對話中搜尋、整理並執行。',
    cards: [['一次使用多個工具', '把聊天中的資訊直接轉到議題追蹤與文件中。'], ['執行前先確認', '寄送郵件、建立議題等會產生變更的操作，須核准後才會執行。'], ['只開啟需要的工具', '每個對話都可以選擇啟用或關閉要用的服務。']],
    flow: { from: '#業務團隊', message: 'B 公司說這個月的月報還沒收到。', to: 'SALES-232', result: 'B 公司月報缺漏詢問' },
    approval: { label: 'MCP 執行權限', title: '將透過 Gmail 寄送郵件。', preview: '將要執行的內容', fields: [['收件者', 'partner@northwind.example'], ['主旨', '10 月聯合研討會日程通知']], run: '執行', deny: '不執行' },
    toggles: { title: '外掛程式連結', items: [['Slack', true], ['Atlassian', true], ['Notion', true], ['Gmail', false], ['Google Calendar', false]] },
  },
  teams: {
    eyebrow: '團隊協作', title: ['整個團隊共用的', 'AI 工作空間。'],
    sub: '建立團隊工作空間並邀請同事。好用的工作方式可以作為技能分享，用量也能一目了然地管理。',
    cards: [['團隊工作空間', '依團隊管理成員、Token 與儲存空間。'], ['分享公司技能', '把重複性工作做成技能，分享給全公司。'], ['私人訊息與群組聊天', '和同事交流的同時，隨時分享成果。'], ['用量儀表板', '查看使用次數、Token 與進行中的工作。']],
    team: { name: '北風業務本部', role: '管理員', members: '成員', tokens: '剩餘 Token', storage: '儲存空間', usage: 'Token 餘量', created: '2026 年 9 月 1 日建立' },
    skills: { tabs: ['我的技能', '參考技能', '分享的技能'], items: [['每週業務報告', '業務營運組 · 分享 38 次'], ['提案審查清單', '策略規劃組 · 分享 21 次'], ['客戶會議摘要', '客戶成功組 · 分享 17 次']] },
    chat: { group: '業務本部 · 下半年策略', messages: [['林志明', '可以分享第 3 章目標表的最新版本嗎？'], ['askyour.ai', '已分享《2026 下半年業務策略》最新版。'], ['陳怡君', '收到，今天開會就用這份。']] },
    dash: { title: '用量儀表板', stats: [['近 30 天使用', '1,284 次'], ['Token 總量', '18.6M'], ['平均耗時', '21.9 秒']], activity: '活動' },
    controls: [['透過邀請連結加入', '一個連結就能邀請團隊成員。'], ['Token 與儲存上限', '依團隊分配並管理用量。'], ['執行前核准', '變更操作只會在確認後執行。'], ['密碼保護的分享', '分享對話時可以設定密碼。']],
  },
  everyday: {
    eyebrow: '日常工作工具', title: ['除了代理，', '還有每天都用的工具。'], sub: '記憶、語音提問、翻譯、掌握產業動態，都在同一個工作空間完成。',
    cards: [['助手記憶', '記住工作脈絡與偏好，內容可自行查看與修改。'], ['即時代理', '用即時語音提問，同時使用搜尋與工具。'], ['翻譯', '運用商務、學術領域與術語表翻譯句子和文件。'], ['資訊', '每天彙整關注領域的新聞、部落格與影片。']],
    memory: { title: '我所了解的內容', items: [['工作脈絡', '每週更新下半年業務策略文件'], ['工作方式', '偏好報告先放摘要與表格']], evidence: '查看 4 項依據' },
    live: { status: '正在聆聽', user: '下午開會前，簡短告訴我 B 公司最近的問題。', agent: '本週 B 公司有 1 個議題：月報缺漏詢問。' },
    translate: { styles: ['一般', '學術', '商務'], source: '請確認下一季聯合提案的日程。', target: 'Please confirm the schedule for next quarter’s joint proposal.', glossary: '術語表' },
    feed: { label: 'DAILY FEED', items: [['新聞', '製造業導入 AI，從文件工作開始快速普及'], ['部落格', 'B2B 業務團隊運用代理的 5 種方式']] },
    more: [['檔案儲存', '上傳與產生的所有檔案集中管理'], ['小工具', '日曆、郵件、備忘錄一次看'], ['工具箱', '計算機、日期、JSON 等小工具'], ['對話分享', '透過連結分享有用的對話']],
  },
  cases: {
    eyebrow: '導入案例', title: ['每家企業的挑戰不同，', '我們提供量身打造的方案。'],
    sub: '從資安要求嚴格的地端部署環境，到企業知識體系化，再到 ERP 整合自動化。以下是依各企業的資安政策與工作方式建置 askyour.ai 的案例。',
    items: [
      { company: 'S 公司', kind: '地端部署', title: '資料不離開企業的 AI 工作空間', body: '針對處理個人資料與內部文件等高敏感資料的環境，以地端方式部署 askyour.ai，並依企業政策進行客製化。所有資料皆在企業內部基礎設施中處理與保存，不會傳送任何資訊至外部 LLM API。', points: ['地端部署與客製化', '資料於內部基礎設施處理', '阻擋傳送至外部 LLM API'] },
      { company: 'P 公司', kind: '知識本體', title: '將分散的隱性知識，化為代理可運用的知識', body: '將散落於各部門與文件中的內部知識與隱性知識建構為本體結構，並提供搭載這些知識的代理框架，讓組織累積的經驗能在工作現場即時發揮作用。', points: ['整合內部文件與隱性知識', '以本體進行知識結構化', '搭載企業知識的代理框架'] },
      { company: 'K 公司', kind: 'ERP 整合自動化', title: '將手動傳票處理交給代理管線', body: '將在 ERP 中手動輸入傳票的重複作業自動化。透過整合 askyour.ai 與 ERP，由代理擷取傳票處理所需的資訊，並將從擷取到輸入 ERP 的完整過程整合為單一自動化管線。', points: ['askyour.ai 與 ERP 整合', '自動擷取傳票資訊', '從擷取到輸入的管線自動化'] },
    ],
    onprem: { title: '部署環境', badge: 'On-premises', rows: [['LLM', '內部模型'], ['文件·資料', '內部儲存']], blocked: ['外部 LLM API', '已阻擋'] },
    ontology: { title: '企業知識本體', docs: ['設備點檢手冊.pdf', '作業標準.hwpx', '會議紀錄.docx'], concepts: ['設備', '製程', '品質標準'], agent: '已搭載至代理' },
    erp: { title: '傳票處理管線', extract: '擷取傳票資訊', fields: [['往來廠商', '北風股份有限公司'], ['金額', 'NT$38,000'], ['會計科目', '文具用品']], done: '已輸入 ERP', voucher: '傳票 2026-0142' },
  },
  faq: {
    eyebrow: '常見問題', title: '導入前的常見問題',
    items: [
      { q: '可以以團隊為單位使用嗎？', a: '可以建立團隊工作空間，並透過邀請連結邀請同事。在團隊管理頁面可以查看與管理成員、Token 和儲存空間。企業導入請透過「洽詢企業方案」與我們聯絡。' },
      { q: '支援哪些檔案？', a: '文件代理可以編輯 Word（DOCX）、Excel（XLSX）、PowerPoint（PPTX），並可檢視 PDF。智慧助手也支援韓文 HWPX 文件的撰寫與編輯、PDF 文字擷取與圖片分析。' },
      { q: '可以連結哪些工作工具？', a: '可以透過外掛程式連結 Slack、Notion、Jira 與 Confluence（Atlassian）、Gmail、Google 日曆、Microsoft Teams、Outlook 郵件與日曆、GitHub、Discord。Google Drive 與 OneDrive 的檔案可以直接附加到對話中。' },
      { q: '代理會擅自執行操作嗎？', a: '寄送郵件、建立議題等會產生變更的操作，預設會在執行前請求核准。確認執行內容後，您可以選擇執行或拒絕。' },
      { q: '如何收費？', a: '使用 Google 帳號註冊即可立即使用免費方案。如需更多用量，可在價格頁面比較各方案。' },
    ],
  },
  closing: { title: ['你的下一項工作，', '與 Askyour.ai 一起完成。'], sub: '免費開始使用，企業級導入隨時與我們聯絡。' },
  footer: { tagline: '把工作做完的 AI 代理工作空間', product: '產品', company: '聯絡我們', legal: '條款', terms: '服務條款', privacy: '隱私權政策', email: '郵件洽詢' },
};

export function getLandingCopy(locale: string): LandingCopy {
  const language = locale.toLowerCase();
  if (language.startsWith('ko')) return ko;
  if (language.startsWith('ja')) return ja;
  if (language.startsWith('zh')) return /tw|hk|hant/.test(language) ? zhTW : zhCN;
  return en;
}
